<template>
  <v-card color="transparent" elevation="0">
    <v-row class="transaction-toolbar-row">
      <v-col cols="12" class="transaction-toolbar d-flex align-center justify-space-between flex-wrap">
        <div class="d-flex align-center flex-wrap mb-2">
          <div class="stats-version-control mr-3">
            <span>Datenbasis</span>
            <v-btn-toggle v-model="selectedStatisticsVersion" mandatory dense dark>
              <v-btn value="v2">
                <v-icon small left>mdi-database-check-outline</v-icon>
                V2 RADB
              </v-btn>
              <v-btn value="v1">V1 bisher</v-btn>
            </v-btn-toggle>
          </div>

          <v-btn-toggle v-model="selectedDataType" mandatory dense dark>
            <v-btn value="invoices">Debitoren</v-btn>
            <v-btn value="payments">Cash-Eingang</v-btn>
          </v-btn-toggle>

          <div class="ml-4 d-flex align-center" v-if="selectedDataType === 'payments'">
            <v-switch v-model="deductVAT" inset dense hide-details color="primary" class="mt-0 pt-0">
              <template v-slot:label>
                <span class="white--text caption font-weight-bold">
                  {{ deductVAT ? "Netto (exkl. 8.1%)" : "Brutto" }}
                </span>
              </template>
            </v-switch>
          </div>
        </div>

        <div class="d-flex align-center flex-wrap mb-2 expense-actions">
          <v-btn class="mr-2" @click="showRecurringManager = true">
            <v-icon left>mdi-sync</v-icon>
            Daueraufträge
          </v-btn>
          <v-btn color="primary" @click="showExpenseDialog = true">
            <v-icon left>mdi-plus-box</v-icon>
            Ausgabe erfassen
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-row v-if="!finishedLoading" class="d-flex justify-center flex-grow py-16">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </v-row>

    <v-row v-else>
      <v-col v-if="selectedStatisticsVersion === 'v2'" cols="12" class="pt-2" :order="forecastRangeIsHistorical ? 2 : 1">
        <v-card class="forecast-shell overflow-hidden" elevation="0">
          <div class="forecast-header pa-5 pa-md-6">
            <div class="d-flex align-start justify-space-between flex-wrap">
              <div class="d-flex align-start mb-3 mb-md-0">
                <div class="forecast-icon mr-4">
                  <v-icon color="#42b8f5" size="30">mdi-chart-timeline-variant-shimmer</v-icon>
                </div>
                <div>
                  <div class="d-flex align-center flex-wrap">
                    <div class="text-overline forecast-accent-text mr-3">TAC-Vertragsprognose</div>
                    <v-chip v-if="forecastSnapshot" x-small outlined :color="forecastSnapshot.stale ? 'amber' : 'green accent-3'" class="mb-1">
                      Stand {{ formatDate(forecastSnapshot.source.asOfDate) }}
                      <span v-if="forecastSnapshot.ageDays !== null"> · {{ forecastSnapshot.ageDays }} Tage alt</span>
                    </v-chip>
                    <v-chip v-if="forecastState && forecastState.activeImport" x-small dark color="#168fcf" class="mb-1 ml-1">
                      <v-progress-circular indeterminate size="11" width="2" class="mr-1"></v-progress-circular>
                      {{ forecastState.activeImport.progress && forecastState.activeImport.progress.message || "Import läuft" }}
                    </v-chip>
                  </div>
                  <h2 class="forecast-title white--text">{{ forecastHeaderTitle }}</h2>
                  <div class="forecast-subtitle mt-1">
                    {{ forecastHeaderSubtitle }}
                  </div>
                </div>
              </div>
              <div class="d-flex flex-wrap forecast-actions">
                <v-btn v-if="!forecastRangeIsHistorical" text color="#42b8f5" :disabled="!forecastSnapshot || !hasConfirmedForecastRows" @click="openForecastDetails()">
                  <v-icon left>mdi-text-box-search-outline</v-icon>
                  Einzelnachweis
                </v-btn>
                <v-btn outlined color="white" class="ml-2" :loading="forecastImporting" @click="openForecastImportDialog">
                  <v-icon left>mdi-database-sync-outline</v-icon>
                  Spiegel einlesen
                </v-btn>
              </div>
            </div>
          </div>

          <v-progress-linear
            v-if="forecastState && forecastState.activeImport"
            :value="forecastImportProgress"
            height="4"
            color="#42b8f5"
            background-color="#181e28"></v-progress-linear>

          <v-alert v-if="forecastSnapshot && forecastSnapshot.stale && !forecastRangeIsHistorical" tile dense type="warning" text class="ma-0 px-5">
            Spiegelstand {{ formatDate(forecastSnapshot.source.asOfDate) }} · spätere TAC-Änderungen fehlen noch.
          </v-alert>
          <v-alert v-if="forecastError" tile dense type="error" text class="ma-0 px-5">
            {{ forecastError }}
          </v-alert>
          <v-alert v-if="forecastBusinessError" tile dense :type="forecastBusinessUpgradeRequired ? 'info' : 'warning'" text class="ma-0 px-5">
            {{ forecastBusinessError }}
            <v-btn v-if="forecastBusinessUpgradeRequired" small text color="#42b8f5" class="ml-2" @click="openForecastImportDialog">Spiegel neu einlesen</v-btn>
          </v-alert>

          <div v-if="forecastLoading" class="forecast-loading py-12 text-center">
            <v-progress-circular indeterminate color="#42b8f5" size="42"></v-progress-circular>
            <div class="grey--text text--lighten-1 mt-3">Prognose und Vergleich werden geladen…</div>
          </div>

          <div v-else-if="!forecastSnapshot" class="pa-6">
            <div class="forecast-empty-schedule mb-0">
              <v-icon color="#42b8f5" class="mr-3">mdi-database-off-outline</v-icon>
              <div>
                <strong>Noch kein RADB-Snapshot eingelesen</strong>
                <span>
                Hinterlege beim ersten Import den tatsächlichen Stand des TAC-Backups. Danach bleiben alle importierten Stände unverändert erhalten.
                </span>
              </div>
            </div>
          </div>

          <template v-else-if="forecastData">
            <div v-if="forecastRangeIsHistorical" class="forecast-history-context mx-5 mx-md-6 my-5">
              <v-icon color="#42b8f5" size="30">mdi-chart-box-outline</v-icon>
              <div>
                <div class="business-eyebrow">Abgeschlossener Zeitraum</div>
                <h3>Hier zählt der bereinigte Ist-Umsatz.</h3>
                <p>Prognose ab {{ formatDate(forecastStartsAt) }} · V2-Ist direkt darunter.</p>
              </div>
            </div>

            <div v-else class="px-5 px-md-6 pt-5">
              <div v-if="forecastRangeIsMixed && forecastBusinessData && forecastBusinessData.forecastRange" class="forecast-range-note mb-4">
                <v-icon small color="#42b8f5" class="mr-2">mdi-calendar-arrow-right</v-icon>
                <span>
                  Prognose nur {{ formatDate(forecastBusinessData.forecastRange.from) }}–{{
                    formatDate(forecastBusinessData.forecastRange.to)
                  }} · davor zählt das Ist.
                </span>
              </div>
              <div class="d-flex align-center justify-space-between flex-wrap mb-3">
                <div class="caption grey--text text--lighten-1">
                  {{ formatDate(forecastData.range.from) }}–{{ formatDate(forecastData.range.to) }} · Brutto
                </div>
                <div v-if="hasConfirmedForecastRows" class="caption grey--text">
                  {{ formatNumber(forecastData.summary.paymentRows) }} TAC-Fälligkeiten
                </div>
              </div>

              <section v-if="forecastBusinessData" class="business-forecast mb-5">
                <div class="business-forecast__head">
                  <div>
                    <div class="business-eyebrow">Vertragsmodell</div>
                    <h3 class="business-title">Gesamterwartung</h3>
                    <div class="business-subtitle">Bestand sichern + Wachstumsziel</div>
                  </div>
                  <div class="business-control">
                    <span>
                      Szenario-Abweichung
                      <v-progress-circular v-if="forecastBusinessLoading" indeterminate size="10" width="2" color="#f2b84b" class="ml-1"></v-progress-circular>
                    </span>
                    <v-btn-toggle
                      v-model="forecastScenarioDeviation"
                      mandatory
                      dense
                      color="#f2b84b"
                      @change="updateForecastScenarioDeviation">
                      <v-btn v-for="value in forecastScenarioDeviationOptions" :key="value" small :value="value">±{{ value }}%</v-btn>
                    </v-btn-toggle>
                  </div>
                </div>

                <div class="business-source-rail" aria-label="Bestandteile der Prognose">
                  <div class="business-source-pill business-source-pill--confirmed" title="Bereits erzeugte TAC-Fälligkeiten">
                    <span class="business-source-dot business-source-dot--confirmed"></span><strong>Bestätigt {{ formatCurrency(forecastBusinessData.summary.confirmedAmount) }}</strong>
                  </div>
                  <div class="business-source-pill business-source-pill--contract" title="Alle fälligen Bestandsverträge; gekündigte Verträge zählen als Rückholziel">
                    <span class="business-source-dot business-source-dot--contract"></span><strong>Bestand fällig {{ formatNumber(forecastBusinessData.summary.renewalCandidates) }}</strong>
                    <em v-if="forecastBusinessData.summary.cancelledRetentionCandidates">· {{ formatNumber(forecastBusinessData.summary.cancelledRetentionCandidates) }} gekündigt</em>
                  </div>
                  <div :class="['business-source-pill', 'business-source-pill--plan', { 'business-source-pill--negative': forecastBusinessData.summary.plannedNetGrowth < 0 }]" title="Geplante Zugänge minus geplante Abgänge im Prognosezeitraum">
                    <span class="business-source-dot business-source-dot--plan"></span><strong>Wachstumsziel {{ formatSignedCount(forecastBusinessData.summary.plannedNetGrowth) }}</strong>
                  </div>
                </div>
                <div
                  v-if="forecastBusinessData.dataQuality.outsideProjectionMonths || forecastBusinessData.dataQuality.missingPlanStudioMonths"
                  class="business-notices mt-3">
                  <div v-if="forecastBusinessData.dataQuality.outsideProjectionMonths">
                    <v-icon small color="amber" class="mr-2">mdi-alert-outline</v-icon>
                    <span>{{ formatNumber(forecastBusinessData.dataQuality.outsideProjectionMonths) }} Monate ausserhalb der Vertragsabdeckung</span>
                  </div>
                  <div v-if="forecastBusinessData.dataQuality.missingPlanStudioMonths">
                    <v-icon small color="amber lighten-1" class="mr-2">mdi-information-outline</v-icon>
                    <span>{{ formatNumber(forecastBusinessData.dataQuality.missingPlanStudioMonths) }} Studio-Monate ohne Wachstumsplan</span>
                  </div>
                </div>

                <v-row dense class="mt-3">
                  <v-col v-for="scenario in forecastBusinessData.scenarios" :key="scenario.key" cols="12" md="4">
                    <button type="button" :class="forecastScenarioClass(scenario.key)" :title="forecastScenarioHint(scenario.key)" @click="openForecastBusinessDetails()">
                      <div class="business-scenario__top">
                        <span>{{ scenario.label }}</span>
                        <v-icon
                          small
                          class="business-scenario__icon">
                          {{ scenario.key === 'cautious' ? 'mdi-weather-cloudy' : scenario.key === 'plan' ? 'mdi-target' : 'mdi-trending-up' }}
                        </v-icon>
                      </div>
                      <div class="business-scenario__amount">{{ formatCurrency(scenario.amount) }}</div>
                      <div class="business-scenario__unit">Gesamterwartung</div>
                      <div class="business-scenario__bar" aria-hidden="true">
                        <span class="business-scenario__bar-confirmed" :style="{ width: forecastScenarioPartWidth(scenario.confirmedAmount, scenario.amount) }"></span>
                        <span class="business-scenario__bar-forecast" :style="{ width: forecastScenarioPartWidth(scenario.forecastAmount, scenario.amount) }"></span>
                      </div>
                      <div class="business-scenario__bridge">
                        <span class="business-scenario__bridge-confirmed">{{ formatCurrency(scenario.confirmedAmount) }} bestätigt</span>
                        <span class="business-scenario__bridge-forecast">{{ formatCurrency(scenario.forecastAmount) }} offen</span>
                      </div>
                    </button>
                  </v-col>
                </v-row>

                <div class="business-facts mt-3">
                  <button type="button" @click="openForecastBusinessDetails({ kind: 'renewal_candidate' })">
                    <span>Verlängerungskandidaten</span>
                    <strong>{{ formatNumber(forecastBusinessData.summary.renewalCandidates) }}</strong>
                    <small>{{ formatNumber(forecastBusinessData.summary.unscheduledRenewals) }} offen · {{ formatNumber(forecastBusinessData.summary.cancelledRetentionCandidates) }} gekündigt</small>
                  </button>
                  <button type="button" @click="openForecastBusinessDetails({ kind: 'known_contract_start', classification: 'new_member' })">
                    <span>Echte Neukunden</span>
                    <strong>{{ formatNumber(forecastBusinessData.summary.newMembers) }}</strong>
                  </button>
                  <button type="button" @click="openForecastBusinessDetails({ kind: 'known_contract_start', classification: 'reactivation' })">
                    <span>Reaktivierungen</span>
                    <strong>{{ formatNumber(forecastBusinessData.summary.reactivations) }}</strong>
                  </button>
                  <div>
                    <span>Ø Abo-Laufzeitwert</span>
                    <strong>{{ formatCurrency(forecastBusinessData.summary.averageSubscriptionPrice) }}</strong>
                    <small>{{ formatNumber(forecastBusinessData.summary.averageSubscriptionPriceContracts) }} Verträge</small>
                  </div>
                  <div>
                    <span>Gratis-Verträge</span>
                    <strong>{{ formatNumber(forecastBusinessData.summary.uniqueComplimentaryContracts) }}</strong>
                    <small>
                      {{ formatNumber(forecastBusinessData.summary.confirmedComplimentaryPaymentRows) }} TAC-Fälligkeiten · 0 CHF
                    </small>
                  </div>
                </div>

                <v-row dense class="mt-3">
                  <v-col cols="12" lg="8">
                    <div class="business-panel fill-height">
                      <div class="business-panel__head">
                        <div>
                          <div class="font-weight-bold white--text">Monatliche Brücke</div>
                        </div>
                        <v-icon color="#42b8f5">mdi-chart-timeline-variant</v-icon>
                      </div>
                      <div class="business-month business-month--head">
                        <span>Monat</span><span>TAC bestätigt</span><span>Verlängerung</span><span>Wachstumsziel</span><span>Gesamterwartung</span><span>Abdeckung</span>
                      </div>
                      <button
                        v-for="month in forecastBusinessData.monthly"
                        :key="month.month"
                        type="button"
                        class="business-month"
                        @click="openForecastBusinessMonth(month.month)">
                        <span class="business-month__name">{{ formatForecastMonth(month.month) }}</span>
                        <span><strong>{{ formatCurrency(month.confirmedAmount) }}</strong><small>{{ formatNumber(month.confirmedPaymentRows) }} Fälligkeiten</small></span>
                        <span><strong>{{ formatNumber(month.renewalCandidates) }}</strong><small>{{ formatNumber(month.unscheduledRenewals) }} nicht erzeugt</small></span>
                        <span :class="month.plannedNetGrowth < 0 ? 'red--text text--lighten-2' : ''"><strong>{{ formatSignedCount(month.plannedNetGrowth) }}</strong><small>{{ formatSignedCount(month.remainingGrowthTarget) }} noch offen</small></span>
                        <span><strong>{{ formatCurrency(month.scenarios.plan.amount) }}</strong><small>{{ formatCurrency(month.scenarios.plan.forecastAmount) }} noch prognostiziert</small></span>
                        <span><em :class="forecastCoverageClass(month.coverageStatus)">{{ forecastCoverageLabel(month.coverageStatus) }}</em><small v-if="month.scheduleCoveragePercent !== null">{{ month.scheduleCoveragePercent.toFixed(1) }}%</small></span>
                      </button>
                    </div>
                  </v-col>
                  <v-col cols="12" lg="4">
                    <div class="business-panel fill-height">
                      <div class="business-panel__head">
                        <div>
                          <div class="font-weight-bold white--text">Wo TAC noch Lücken hat</div>
                          <div class="caption grey--text">Produktgenau gegen Vertragsenden geprüft</div>
                        </div>
                        <v-icon color="amber lighten-1">mdi-database-alert-outline</v-icon>
                      </div>
                      <div class="business-gap-note"><span></span>Rot = noch ohne TAC-Fälligkeit</div>
                      <div class="business-product-gaps">
                        <button
                          v-for="product in forecastBusinessData.productCoverage.slice(0, 8)"
                          :key="`${product.categoryGroup}-${product.productName}`"
                          type="button"
                          @click="openForecastBusinessDetails({ kind: 'renewal_candidate', scheduleStatus: 'unscheduled', search: product.productName, categoryGroup: product.categoryGroup })">
                          <span>
                            <strong>{{ product.productName }}</strong>
                            <small v-if="product.isComplimentary">{{ product.categoryGroup }} · Gratis · keine Rechnung</small>
                            <small v-else>{{ product.categoryGroup }} · Ø {{ formatCurrency(product.averageTermPrice) }}</small>
                          </span>
                          <span class="business-gap-count">
                            <strong>{{ formatNumber(product.unscheduledRenewals) }}</strong>
                            <small>von {{ formatNumber(product.renewalCandidates) }} offen</small>
                          </span>
                        </button>
                        <div v-if="!forecastBusinessData.productCoverage.length" class="grey--text text-center py-8">Keine Verlängerungskandidaten in dieser Auswahl.</div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </section>

              <template v-if="hasConfirmedForecastRows">
              <div class="forecast-section-label mt-1 mb-3">
                <span>TAC-Fälligkeiten</span>
                <small>bestätigt · inklusive Rabatten</small>
              </div>

              <v-row dense>
                <v-col cols="12" sm="6" lg="3">
                  <button type="button" class="forecast-kpi forecast-kpi--primary" @click="openForecastDetails()">
                    <div class="forecast-kpi__label">Erwarteter Betrag</div>
                    <div class="forecast-kpi__value">{{ formatCurrency(forecastData.summary.totalAmount) }}</div>
                    <div class="forecast-kpi__meta">nach aktiven Rabatten · {{ formatNumber(forecastData.summary.paymentRows) }} Fälligkeiten</div>
                  </button>
                </v-col>
                <v-col cols="12" sm="6" lg="3">
                  <button type="button" class="forecast-kpi" @click="openForecastDetails({ kind: 'renewal' })">
                    <div class="forecast-kpi__label">TAC-Verlängerungsfälligkeiten</div>
                    <div class="forecast-kpi__value">{{ formatCurrency(forecastData.summary.renewalAmount) }}</div>
                    <div class="forecast-kpi__meta">{{ formatNumber(forecastData.summary.renewalRows) }} bereits erzeugte Fälligkeiten</div>
                  </button>
                </v-col>
                <v-col cols="12" sm="6" lg="3">
                  <button type="button" class="forecast-kpi" @click="openForecastDetails({ kind: 'new_contract' })">
                    <div class="forecast-kpi__label">Technische Erstperioden</div>
                    <div class="forecast-kpi__value">{{ formatCurrency(forecastData.summary.newContractAmount) }}</div>
                    <div class="forecast-kpi__meta">{{ formatNumber(forecastData.summary.newContractRows) }} erste Perioden · kein Neukundenbeweis</div>
                  </button>
                </v-col>
                <v-col cols="12" sm="6" lg="3">
                  <button type="button" class="forecast-kpi" @click="openForecastDetails({ kind: 'installment' })">
                    <div class="forecast-kpi__label">Laufende Raten</div>
                    <div class="forecast-kpi__value">{{ formatCurrency(forecastData.summary.installmentAmount) }}</div>
                    <div class="forecast-kpi__meta">{{ formatNumber(forecastData.summary.installmentRows) }} Raten innerhalb laufender Perioden</div>
                  </button>
                </v-col>
              </v-row>

              <div class="forecast-audit-strip mt-3 mb-2">
                <div>
                  <span>Basis vor Aktionen</span>
                  <strong>{{ formatCurrency(forecastData.summary.baseAmount) }}</strong>
                </div>
                <div>
                  <span>Firmenrabatte / Aktionen</span>
                  <strong class="amber--text text--lighten-1">{{ formatCurrency(forecastData.summary.discountAmount) }}</strong>
                </div>
                <button type="button" @click="openForecastDetails({ status: 'excluded' })">
                  <span>Ausgeschlossen</span>
                  <strong>{{ formatNumber(forecastData.excluded.paymentRows) }} Fälligkeiten · {{ formatCurrency(forecastData.excluded.amount) }}</strong>
                </button>
                <div>
                  <span>Ungeklärte Preise</span>
                  <strong :class="forecastData.summary.unresolvedRows ? 'red--text text--lighten-2' : 'green--text text--accent-3'">
                    {{ formatNumber(forecastData.summary.unresolvedRows) }}
                  </strong>
                </div>
              </div>

              <v-alert
                v-if="forecastSnapshot.forecastCoverage && forecastSnapshot.forecastCoverage.unmappedStudioRows"
                dense
                text
                type="warning"
                class="mt-3 mb-2">
                {{ formatNumber(forecastSnapshot.forecastCoverage.unmappedStudioRows) }} TAC-Zeilen konnten keinem Roody-Studio zugeordnet werden und erscheinen daher nicht in einer Studioauswahl.
              </v-alert>

              <v-row dense class="mt-1">
                <v-col cols="12" lg="7">
                  <v-card class="forecast-panel fill-height pa-4" elevation="0">
                    <div class="d-flex align-center justify-space-between mb-4">
                      <div>
                        <div class="font-weight-bold white--text">Fälligkeiten pro Monat</div>
                        <div class="caption grey--text">Nettobetrag nach den verknüpften Aktionszeilen</div>
                      </div>
                      <v-icon color="#42b8f5">mdi-calendar-month-outline</v-icon>
                    </div>
                    <div v-if="forecastData.monthly.length">
                      <button
                        v-for="month in forecastData.monthly"
                        :key="month.month"
                        type="button"
                        class="forecast-month"
                        @click="openForecastMonth(month.month)">
                        <span class="forecast-month__name">{{ formatForecastMonth(month.month) }}</span>
                        <span class="forecast-month__bar"><span :style="{ width: forecastMonthWidth(month.amount) }"></span></span>
                        <span class="forecast-month__count">{{ formatNumber(month.paymentRows) }}</span>
                        <span class="forecast-month__amount">{{ formatCurrency(month.amount) }}</span>
                      </button>
                    </div>
                    <div v-else class="grey--text text-center py-8">Im gewählten Zeitraum liegen keine aktiven Fälligkeiten.</div>
                  </v-card>
                </v-col>

                <v-col cols="12" lg="5">
                  <v-card class="forecast-panel fill-height pa-4" elevation="0">
                    <div class="d-flex align-center justify-space-between mb-3">
                      <div>
                        <div class="font-weight-bold white--text">Preissicherheit</div>
                        <div class="caption grey--text">Quelle jedes prognostizierten Betrags</div>
                      </div>
                      <div class="forecast-quality">{{ forecastHighConfidencePercent.toFixed(1) }}%</div>
                    </div>
                    <v-progress-linear :value="forecastHighConfidencePercent" height="8" rounded color="green accent-3" background-color="#25354a"></v-progress-linear>
                    <div class="d-flex justify-space-between caption grey--text mt-2 mb-4">
                      <span>{{ formatNumber(forecastData.summary.highConfidenceRows) }} direkt/hoch</span>
                      <span>{{ formatNumber(forecastData.summary.unresolvedRows) }} ungeklärt</span>
                    </div>
                    <div class="forecast-source-list">
                      <div v-for="source in forecastData.valueSources" :key="source.name" class="forecast-source-row">
                        <div>
                          <div class="white--text body-2">{{ forecastSourceLabel(source.name) }}</div>
                          <div class="caption grey--text">{{ formatNumber(source.paymentRows) }} Fälligkeiten</div>
                        </div>
                        <div class="text-right white--text font-weight-medium">{{ formatCurrency(source.amount) }}</div>
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <v-row dense class="mt-1">
                <v-col cols="12" lg="7">
                  <v-card class="forecast-panel fill-height" elevation="0">
                    <div class="d-flex align-center justify-space-between pa-4 pb-2">
                      <div>
                        <div class="font-weight-bold white--text">Abos & Pakete mit grösstem Volumen</div>
                        <div class="caption grey--text">Klick öffnet die einzelnen TAC-Fälligkeiten</div>
                      </div>
                      <v-icon color="purple lighten-2">mdi-package-variant-closed-check</v-icon>
                    </div>
                    <v-data-table
                      :headers="forecastProductHeaders"
                      :items="forecastData.products.slice(0, 10)"
                      dark
                      dense
                      hide-default-footer
                      disable-pagination
                      class="transparent forecast-table"
                      @click:row="openForecastProduct">
                      <template v-slot:item.name="{ item }">
                        <div class="font-weight-medium white--text">{{ item.name }}</div>
                        <div class="caption grey--text">{{ item.main }} · {{ item.sub }}</div>
                      </template>
                      <template v-slot:item.paymentRows="{ item }">{{ formatNumber(item.paymentRows) }}</template>
                      <template v-slot:item.amount="{ item }"><span class="font-weight-bold">{{ formatCurrency(item.amount) }}</span></template>
                    </v-data-table>
                  </v-card>
                </v-col>

                <v-col cols="12" lg="5">
                  <v-card class="forecast-panel fill-height" elevation="0">
                    <div class="d-flex align-center justify-space-between pa-4 pb-2">
                      <div>
                        <div class="font-weight-bold white--text">Firmenrabatte & Spezialaktionen</div>
                        <div class="caption grey--text">TAC-Aktionsname, Umfang und betroffene Verträge</div>
                      </div>
                      <v-icon color="amber lighten-1">mdi-sale-outline</v-icon>
                    </div>
                    <v-data-table
                      :headers="forecastPromotionHeaders"
                      :items="forecastData.promotions.slice(0, 10)"
                      dark
                      dense
                      hide-default-footer
                      disable-pagination
                      class="transparent forecast-table"
                      @click:row="openForecastPromotion">
                      <template v-slot:item.name="{ item }">
                        <div class="font-weight-medium white--text">{{ item.name }}</div>
                        <div v-if="item.code || item.membershipGroupId" class="caption grey--text">
                          <span v-if="item.code">Code {{ item.code }}</span>
                          <span v-if="item.membershipGroupId"> · Gruppe {{ item.membershipGroupId }}</span>
                        </div>
                      </template>
                      <template v-slot:item.affectedContracts="{ item }">{{ formatNumber(item.affectedContracts) }}</template>
                      <template v-slot:item.amount="{ item }"><span class="amber--text text--lighten-1">{{ formatCurrency(item.amount) }}</span></template>
                    </v-data-table>
                    <div v-if="!forecastData.promotions.length" class="grey--text text-center py-8">Keine Aktionszeilen in dieser Auswahl.</div>
                  </v-card>
                </v-col>
              </v-row>
              </template>
              <div v-else class="forecast-empty-schedule my-4">
                <v-icon color="#42b8f5" class="mr-3">mdi-calendar-question-outline</v-icon>
                <div>
                  <strong>Noch keine TAC-Fälligkeiten</strong>
                  <span>Bestand und Wachstumsziel bleiben oben sichtbar.</span>
                </div>
              </div>
            </div>

            <div class="forecast-comparison mx-5 mx-md-6 mt-4 mb-5 pa-4" v-if="forecastComparison && forecastComparison.coverage.valid">
              <div class="d-flex align-center justify-space-between flex-wrap">
                <div class="d-flex align-center mr-4 mb-2 mb-md-0">
                  <v-icon color="#b59cf6" class="mr-3">mdi-compare-horizontal</v-icon>
                  <div>
                    <div class="font-weight-bold white--text">V1 gegen RADB V2 · {{ selectedDataType === 'payments' ? 'Cash-Eingang' : 'Debitoren' }}</div>
                    <div class="caption grey--text">
                      Nur {{ formatNumber(forecastComparison.coverage.pairedSeries) }} gemeinsam vorhandene Tages-/Studiozeilen bis {{ formatDate(forecastComparison.coverage.to) }}
                    </div>
                  </div>
                </div>
                <div class="d-flex align-center flex-wrap comparison-values">
                  <div><span>V1</span><strong>{{ formatCurrency(forecastComparison.summary.v1Total) }}</strong></div>
                  <div><span>V2 bereinigt</span><strong>{{ formatCurrency(forecastComparison.summary.v2Total) }}</strong></div>
                  <div><span>Differenz</span><strong :class="comparisonDifferenceClass">{{ formatSignedCurrency(forecastComparison.summary.difference) }}</strong></div>
                  <div><span>Storno erkannt</span><strong class="amber--text text--lighten-1">{{ formatCurrency(forecastComparison.summary.cancellationExcludedTotal) }}</strong></div>
                  <v-btn small text color="#b59cf6" class="ml-2" @click="openForecastComparison">Vergleich öffnen</v-btn>
                </div>
              </div>
            </div>
          </template>
        </v-card>
      </v-col>

      <template v-if="selectedStatisticsVersion === 'v2'">
        <v-col cols="12" :order="forecastRangeIsHistorical ? 1 : 2">
          <section class="v2-actual-section">
            <div class="v2-actual-head">
              <div>
                <div class="business-eyebrow">Ist-Statistik · V2</div>
                <h2>{{ forecastMixActive ? "Ist + Prognose" : "Bereinigter Umsatz" }}</h2>
                <p>Stornobereinigtes Ist aus dem RADB-Spiegel.</p>
              </div>
              <div class="v2-actual-actions">
                <v-switch
                  v-if="!forecastRangeIsHistorical"
                  v-model="includeForecastInActuals"
                  :disabled="forecastBusinessLoading || !forecastMixAvailable"
                  inset
                  dense
                  hide-details
                  color="#42b8f5"
                  class="v2-forecast-switch"
                  title="Addiert die normale Plan-Prognose ab dem Tag nach dem Spiegelstand">
                  <template v-slot:label><span>Prognose einrechnen</span></template>
                </v-switch>
                <div
                  v-if="actualV2Data"
                  class="v2-source-badge"
                  :title="`${formatNumber(actualV2Data.summary.sourceRows)} TAC-Quellzeilen · ${formatNumber(actualV2Data.summary.dailyStudioRows)} Tages-/Studiozeilen`">
                  <v-icon small color="#56c88b">mdi-database-check-outline</v-icon>
                  <span>{{ formatDate(actualV2Data.snapshot.source.asOfDate) }}</span>
                </div>
              </div>
            </div>

            <div v-if="statisticsError" class="v2-status-note v2-status-note--error mt-4">
              <v-icon small color="red lighten-2" class="mr-2">mdi-alert-circle-outline</v-icon>
              <span>{{ statisticsError }} Nur die V2-Ist-Abfrage ist betroffen; Prognose und V1 sind davon getrennte Datenquellen.</span>
            </div>
            <div v-else-if="actualV2Data && !actualV2Data.coverage.valid" class="v2-status-note mt-4">
              <v-icon small color="amber" class="mr-2">mdi-calendar-remove-outline</v-icon>
              <span v-if="actualV2Data.coverage.status === 'after_coverage'">
                Noch kein Ist in diesem Zeitraum. Die Prognose kann über den Schalter eingerechnet werden.
              </span>
              <span v-else>
                Ausserhalb der V2-Abdeckung {{ formatDate(actualV2Data.coverage.coverageFrom) }}–{{ formatDate(actualV2Data.coverage.coverageTo) }} · für ältere Zeiträume V1 nutzen.
              </span>
            </div>
            <div v-else-if="actualV2Data && !actualV2Data.coverage.complete" class="v2-status-note mt-4">
              <v-icon small color="amber" class="mr-2">mdi-calendar-alert-outline</v-icon>
              <span>
                Der Spiegel deckt {{ formatDate(actualV2Data.coverage.coverageFrom) }}–{{ formatDate(actualV2Data.coverage.coverageTo) }} ab. Angezeigt wird nur der davon verfügbare Anteil.
              </span>
            </div>

            <div class="v2-kpi-grid mt-4">
              <div :class="['v2-kpi', 'v2-kpi--primary', { 'v2-kpi--mixed': forecastMixActive }]">
                <span>{{ forecastMixActive ? "Ist + Prognose" : "Bereinigter Umsatz" }}</span>
                <strong>{{ hasDisplayedRevenue ? formatCurrency(displayRevenue) : "–" }}</strong>
                <div v-if="forecastMixActive" class="v2-revenue-mix">
                  <div class="v2-revenue-mix__bar" aria-hidden="true">
                    <span class="v2-revenue-mix__actual" :style="{ width: `${revenueMixActualPercent}%` }"></span>
                    <span class="v2-revenue-mix__forecast" :style="{ width: `${revenueMixForecastPercent}%` }"></span>
                  </div>
                  <div class="v2-revenue-mix__labels">
                    <span><i class="v2-color-dot v2-color-dot--actual"></i>Ist {{ formatCurrency(displayActualRevenue) }}</span>
                    <span><i class="v2-color-dot v2-color-dot--forecast"></i>Prognose {{ formatCurrency(displayForecastRevenue) }}</span>
                  </div>
                </div>
                <small v-else>{{ selectedDataType === "payments" ? "Cash-Eingang" : "Debitoren" }} · Storno entfernt</small>
              </div>
              <button type="button" class="v2-kpi" @click="showInvoiceDialog = true">
                <span>Erfasste Ausgaben</span>
                <strong>{{ formatCurrency(invoiceTotal) }}</strong>
                <small>Rechnungsanalyse öffnen</small>
              </button>
              <div class="v2-kpi">
                <span>{{ forecastMixActive ? "Erwarteter Gewinn" : "Geschätzter Gewinn" }}</span>
                <strong>{{ hasDisplayedRevenue ? formatCurrency(estimatedProfit) : "–" }}</strong>
                <small>Umsatz − Ausgaben</small>
              </div>
              <div class="v2-kpi">
                <span>Ist-Transaktionen</span>
                <strong>{{ data ? formatNumber(data.totalTransactions) : "–" }}</strong>
                <small>stornobereinigt</small>
              </div>
              <div class="v2-kpi">
                <span>Ø Ist-Transaktion</span>
                <strong>{{ data && actualV2Data ? formatCurrency(getEffectiveValue(actualV2Data.summary.averageTransactionValue)) : "–" }}</strong>
              </div>
              <button type="button" class="v2-kpi" :disabled="!(actualV2Data && actualV2Data.coverage.valid && forecastComparison)" @click="openForecastComparison">
                <span>Storno entfernt</span>
                <strong>{{ data && actualV2Data ? formatCurrency(getEffectiveValue(actualV2Data.summary.cancellationExcludedTotal)) : "–" }}</strong>
                <small>{{ data && actualV2Data ? formatNumber(actualV2Data.summary.cancellationExcludedCount) : "–" }} Zeilen · Nachweis</small>
              </button>
            </div>

            <template v-if="actualV2Data && actualV2Data.coverage.valid">
              <div class="v2-actual-meta mt-3">
                <div>
                  <span>Verwendeter Zeitraum</span>
                  <strong>{{ formatDate(actualV2Data.coverage.from) }}–{{ formatDate(actualV2Data.coverage.to) }}</strong>
                </div>
                <div>
                  <span>Vorjahresvergleich</span>
                  <strong v-if="actualV2Data.coverage.complete && actualV2Data.previousYear.coverage.complete" :class="comparisonToPreviousYear.trend === 'up' ? 'forecast-accent-text' : comparisonToPreviousYear.trend === 'down' ? 'red--text text--lighten-2' : ''">
                    {{ comparisonToPreviousYear.percentage > 0 ? "+" : "" }}{{ comparisonToPreviousYear.percentage !== null ? comparisonToPreviousYear.percentage.toFixed(1) + "%" : "–" }}
                  </strong>
                  <strong v-else>nicht vollständig abgedeckt</strong>
                </div>
              </div>

              <v-row dense class="mt-4">
                <v-col cols="12" lg="7">
                  <div class="v2-actual-panel fill-height pa-4">
                    <div class="v2-panel-title">
                      <div>
                        <strong>Bereinigter Verlauf</strong>
                        <span>Ist-Buchungen</span>
                      </div>
                      <v-icon color="#56c88b">mdi-chart-line</v-icon>
                    </div>
                    <apexchart v-if="actualV2Data.daily.length" height="320" type="area" :series="revenueOverTimeSeries" :options="v2RevenueOverTimeOptions"></apexchart>
                    <div v-else class="v2-empty py-12">Keine V2-Ist-Buchungen im abgedeckten Teil dieser Auswahl.</div>
                  </div>
                </v-col>
                <v-col cols="12" lg="5">
                  <div class="v2-actual-panel fill-height">
                    <div class="v2-panel-title pa-4 pb-2">
                      <div>
                        <strong>Studios im Vergleich</strong>
                      </div>
                      <v-icon color="#b59cf6">mdi-map-marker-multiple-outline</v-icon>
                    </div>
                    <v-data-table
                      :headers="actualV2StudioHeaders"
                      :items="actualV2Data.byStudio"
                      dark
                      dense
                      fixed-header
                      height="320"
                      :items-per-page="8"
                      :footer-props="{ 'items-per-page-options': [8, 15, -1] }"
                      class="transparent v2-studio-table">
                      <template v-slot:item.totalRevenue="{ item }"><strong>{{ formatCurrency(getEffectiveValue(item.totalRevenue)) }}</strong></template>
                      <template v-slot:item.totalTransactions="{ item }">{{ formatNumber(item.totalTransactions) }}</template>
                      <template v-slot:item.cancellationExcludedTotal="{ item }"><span class="amber--text text--lighten-1">{{ formatCurrency(getEffectiveValue(item.cancellationExcludedTotal)) }}</span></template>
                    </v-data-table>
                    <div v-if="!actualV2Data.byStudio.length" class="v2-empty py-12">Keine Studiozeilen in diesem Zeitraum.</div>
                  </div>
                </v-col>
              </v-row>
            </template>
          </section>
        </v-col>
      </template>

      <template v-else-if="data && data.totalRevenue > 0">
        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card" @click="openYoyDialog">
            <div class="d-flex align-center mb-1">
              <v-icon large color="green accent-4">mdi-cash-multiple</v-icon>
              <div class="ml-4 flex-grow-1">
                <div class="d-flex justify-space-between align-center">
                  <div class="caption grey--text">Gesamtumsatz</div>
                  <v-chip v-if="deductVAT && selectedDataType === 'payments'" x-small color="green accent-4" outlined label class="px-1">
                    Netto
                  </v-chip>
                </div>
                <div class="h4 font-weight-bold white--text">
                  <number :from="0" :to="getEffectiveValue(data.totalRevenue)" :format="formatCurrency"></number>
                </div>
                <div
                  v-if="deductVAT && selectedDataType === 'payments'"
                  class="caption red--text text--accent-2 mt-1"
                  style="line-height: 1.2; font-size: 0.7rem">
                  davon MwSt: -{{ formatCurrency(getVatAmount(data.totalRevenue)) }}
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card" hover @click="showInvoiceDialog = true">
            <div class="d-flex align-center">
              <v-icon large color="red accent-3">mdi-file-chart-outline</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Ausgaben (Rechn.)</div>
                <div class="h4 font-weight-bold white--text">
                  <number :from="0" :to="invoiceTotal" :format="formatCurrency"></number>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center">
              <v-icon large color="light-green accent-3">mdi-scale-balance</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Geschätzter Gewinn</div>
                <div class="h4 font-weight-bold white--text">
                  <number :from="0" :to="estimatedProfit" :format="formatCurrency"></number>
                </div>
                <div v-if="deductVAT && selectedDataType === 'payments'" class="caption grey--text mt-1" style="font-size: 0.7rem">
                  (Netto-Umsatz - Ausgaben)
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center">
              <v-icon large color="blue lighten-1">mdi-receipt-text-check-outline</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Transaktionen</div>
                <div class="h4 font-weight-bold white--text">
                  <number :from="0" :to="data.totalTransactions"></number>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center">
              <v-icon
                large
                :color="
                  comparisonToPreviousYear.trend === 'up' ? 'green accent-4' : comparisonToPreviousYear.trend === 'down' ? 'red accent-3' : 'grey'
                ">
                {{
                  comparisonToPreviousYear.trend === "up"
                    ? "mdi-trending-up"
                    : comparisonToPreviousYear.trend === "down"
                    ? "mdi-trending-down"
                    : "mdi-trending-neutral"
                }}
              </v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Vergleich Vorjahr</div>
                <div v-if="comparisonToPreviousYear.percentage !== null" class="h4 font-weight-bold white--text">
                  <span v-if="comparisonToPreviousYear.percentage === Infinity"> +∞ % </span>
                  <span v-else> {{ comparisonToPreviousYear.percentage > 0 ? "+" : "" }}{{ comparisonToPreviousYear.percentage.toFixed(1) }} % </span>
                </div>
                <div v-else class="h4 font-weight-bold grey--text">N/A</div>
              </div>
            </div>
            <div v-if="data.previousYearTotalRevenue > 0" class="caption grey--text text-right mt-2">
              VJ: {{ formatCurrency(getEffectiveValue(data.previousYearTotalRevenue)) }}
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center">
              <v-icon large color="teal lighten-1">mdi-account-group</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Einzigartige Kunden</div>
                <div class="h4 font-weight-bold white--text">
                  <number :from="0" :to="data.uniqueCustomerCount || 0"></number>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="12" md="4" lg="2" @click="showDetailsDialog = true">
          <v-card class="pa-3 fill-height rounded-lg kpi-card" hover>
            <div class="d-flex align-center">
              <v-icon large color="purple lighten-2">mdi-file-document-outline</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Details</div>
                <div class="h4 font-weight-bold white--text">Alle Anzeigen...</div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="4" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center mb-1">
              <v-icon large color="deep-purple lighten-1">mdi-file-sign</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Umsatz Abos</div>
                <div class="h4 font-weight-bold white--text d-flex align-baseline">
                  <number :to="getEffectiveValue(revenueByCategoryType.subscriptions)" :format="formatCurrency"></number>
                  <span class="caption ml-2 grey--text">({{ revenuePercentages.subscriptions }}%)</span>
                </div>
                <div
                  v-if="deductVAT && selectedDataType === 'payments'"
                  class="caption red--text text--accent-2 mt-1"
                  style="line-height: 1.2; font-size: 0.7rem">
                  MwSt: -{{ formatCurrency(getVatAmount(revenueByCategoryType.subscriptions)) }}
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="4" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center mb-1">
              <v-icon large color="orange darken-1">mdi-package-variant-closed</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Umsatz Pakete</div>
                <div class="h4 font-weight-bold white--text d-flex align-baseline">
                  <number :to="getEffectiveValue(revenueByCategoryType.packages)" :format="formatCurrency"></number>
                  <span class="caption ml-2 grey--text">({{ revenuePercentages.packages }}%)</span>
                </div>
                <div
                  v-if="deductVAT && selectedDataType === 'payments'"
                  class="caption red--text text--accent-2 mt-1"
                  style="line-height: 1.2; font-size: 0.7rem">
                  MwSt: -{{ formatCurrency(getVatAmount(revenueByCategoryType.packages)) }}
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="4" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center mb-1">
              <v-icon large color="grey">mdi-shape-outline</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Umsatz Sonstiges</div>
                <div class="h4 font-weight-bold white--text d-flex align-baseline">
                  <number :to="getEffectiveValue(revenueByCategoryType.other)" :format="formatCurrency"></number>
                  <span class="caption ml-2 grey--text">({{ revenuePercentages.other }}%)</span>
                </div>
                <div
                  v-if="deductVAT && selectedDataType === 'payments'"
                  class="caption red--text text--accent-2 mt-1"
                  style="line-height: 1.2; font-size: 0.7rem">
                  MwSt: -{{ formatCurrency(getVatAmount(revenueByCategoryType.other)) }}
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card class="pa-3 fill-height rounded-lg kpi-card">
            <div class="d-flex align-center">
              <v-icon large color="light-blue accent-3">mdi-account-cash</v-icon>
              <div class="ml-4">
                <div class="caption grey--text">Ø Umsatz / Kunde</div>
                <div class="h4 font-weight-bold white--text">
                  <number :to="getEffectiveValue(avgRevenuePerCustomer)" :format="formatCurrency"></number>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" class="mt-4">
          <v-card class="pa-4 rounded-lg" color="#1F2937">
            <apexchart height="350" type="area" :series="revenueOverTimeSeries" :options="revenueOverTimeOptions"></apexchart>
          </v-card>
        </v-col>

        <v-col cols="12" class="mt-4">
          <v-card class="pa-4 rounded-lg" color="#1F2937">
            <div class="d-flex justify-space-between align-center mb-4 flex-wrap">
              <v-card-title class="white--text pa-0">{{ chartTitle }}</v-card-title>
              <div class="d-flex align-center">
                <v-btn v-if="drilldownLevel > 0" @click="drillUp" small icon color="primary" class="mr-4">
                  <v-icon>mdi-arrow-up</v-icon>
                </v-btn>
                <v-select
                  :items="mainCategorySelectItems"
                  item-text="name"
                  item-value="name"
                  v-model="selectedMainCategory"
                  label="Hauptkategorie wählen"
                  dense
                  outlined
                  dark
                  hide-details
                  style="max-width: 250px"></v-select>
              </div>
            </div>
            <apexchart
              v-if="dynamicChartSeries.length > 0"
              height="400"
              type="bar"
              :series="[{ name: 'Umsatz', data: dynamicChartSeries }]"
              :options="dynamicChartOptions"
              @dataPointSelection="handleChartClick"></apexchart>
            <div v-else class="text-center grey--text py-16">Für diese Kategorie gibt es keine Daten im ausgewählten Zeitraum.</div>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card class="pa-4 rounded-lg fill-height" color="#1F2937">
            <apexchart height="350" type="donut" :series="mainCategorySeries.series" :options="mainCategoryOptions"></apexchart>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="pa-4 rounded-lg fill-height" color="#1F2937">
            <apexchart height="350" type="pie" :series="genderSeries.series" :options="genderOptions"></apexchart>
          </v-card>
        </v-col>
      </template>

      <template v-else>
        <v-col cols="12" class="text-center py-16">
          <h2 v-if="statisticsError" class="red--text text--lighten-2 font-weight-light">{{ statisticsError }}</h2>
          <h2 v-else class="white--text font-weight-light">
            Für den ausgewählten Zeitraum wurden keine Daten für "{{ selectedDataType === "invoices" ? "Debitoren" : "Cash-Eingang" }}" gefunden.
          </h2>
        </v-col>
      </template>
    </v-row>

    <v-dialog v-model="showRecurringManager" fullscreen hide-overlay transition="dialog-bottom-transition">
      <RecurringExpenseManager
        v-if="showRecurringManager"
        :studios="studios"
        :invoice-issuers="invoiceIssuers"
        @close="showRecurringManager = false" />
    </v-dialog>

    <v-row no-gutters>
      <v-col cols="12" class="mt-4">
        <v-card class="pa-2 rounded-lg" color="#1F2937">
          <v-card-title class="white--text pa-0 mb-4">Analyse der Promotionen & Gutschriften</v-card-title>

          <v-data-table
            :headers="[
              { text: 'Promotion / Gutschrift', value: 'name', width: '60%' },
              { text: 'Anzahl Nutzungen', value: 'count', align: 'end' },
              { text: 'Gesamtwert', value: 'totalValue', align: 'end' },
            ]"
            :items="adjustmentsSummary"
            :loading="adjustmentsLoading"
            no-data-text="Keine Promotionsdaten im ausgewählten Zeitraum gefunden."
            dark
            class="elevation-0 transparent"
            hide-default-footer
            disable-pagination>
            <template v-slot:item.totalValue="{ item }">
              <span class="font-weight-bold">{{ formatCurrency(item.totalValue) }}</span>
            </template>

            <template v-slot:body.append>
              <tr class="font-weight-bold" style="background-color: rgba(59, 130, 246, 0.1)">
                <td class="text-right white--text title">Gesamt:</td>
                <td class="text-right white--text title pr-4">{{ adjustmentsTotal.count }}</td>
                <td class="text-right white--text title pr-4">{{ formatCurrency(adjustmentsTotal.totalValue) }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="false">
      <v-col cols="12" md="6">
        <v-card class="pa-4 rounded-lg" color="#1F2937">
          <v-card-title class="white--text pa-0 mb-4">Top 5 nach Umsatz</v-card-title>
          <v-list dense color="transparent" class="pa-0">
            <v-list-item v-for="(item, i) in top5ByRevenue" :key="`rev-${i}`" class="white--text">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="font-weight-bold">
                {{ formatCurrency(getEffectiveValue(item.total)) }}
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card class="pa-4 rounded-lg" color="#1F2937">
          <v-card-title class="white--text pa-0 mb-4">Top 5 nach Verkäufen</v-card-title>
          <v-list dense color="transparent" class="pa-0">
            <v-list-item v-for="(item, i) in top5ByCount" :key="`count-${i}`" class="white--text">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="font-weight-bold"> {{ item.count }} Stk. </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showDetailsDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card color="#111827">
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="showDetailsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>Detailansicht der {{ selectedDataType === "invoices" ? "Debitoren" : "Cash-Eingang" }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-card color="#1F2937" class="pa-4 mb-4 rounded-lg">
            <v-row dense>
              <v-col cols="12" md="3"
                ><v-text-field v-model="filters.customer" label="Nach Kunde filtern..." dark dense outlined clearable hide-details></v-text-field
              ></v-col>
              <v-col cols="12" md="3"
                ><v-autocomplete
                  v-model="filters.contractName"
                  :items="uniqueContractNames"
                  label="Nach Vertragsname filtern..."
                  dark
                  dense
                  outlined
                  clearable
                  hide-details></v-autocomplete
              ></v-col>
              <v-col cols="12" md="3"
                ><v-text-field
                  v-model="filters.description"
                  label="Nach Beschreibung filtern..."
                  dark
                  dense
                  outlined
                  clearable
                  hide-details></v-text-field
              ></v-col>
              <v-col cols="12" md="3"
                ><v-autocomplete
                  v-model="filters.accountName"
                  :items="uniqueAccountNames"
                  label="Nach Konto filtern..."
                  dark
                  dense
                  outlined
                  clearable
                  hide-details></v-autocomplete
              ></v-col>
              <v-col cols="12" md="3" class="d-flex align-center">
                <v-checkbox v-model="filters.hasAdjustment" label="Nur mit Korrektur" dark hide-details class="mt-0 pt-0"></v-checkbox>
              </v-col>
            </v-row>
          </v-card>

          <v-data-table
            :headers="detailsHeaders"
            :items="filteredDetailedTransactions"
            :loading="detailsLoading"
            :server-items-length="totalDetails"
            :options.sync="paginationOptions"
            @update:options="fetchTransactionDetails"
            dark
            class="elevation-2 transparent">
            <template v-slot:item.BILLDATE="{ item }">{{ new Date(item.BILLDATE).toLocaleString("de-CH") }}</template>
            <template v-slot:item.VALUE="{ item }">
              <div v-if="!item.adjustment" class="font-weight-bold">
                {{ formatCurrency(Math.abs(getEffectiveValue(item.VALUE))) }}
              </div>
              <div v-else>
                <del class="grey--text text--lighten-1">{{ formatCurrency(Math.abs(getEffectiveValue(item.VALUE))) }}</del>

                <div class="d-flex justify-end align-center">
                  <v-icon small color="green accent-4" class="mr-1">mdi-plus-circle-outline</v-icon>
                  <span class="green--text text--accent-4">{{ formatCurrency(getEffectiveValue(item.adjustment.value)) }}</span>
                </div>
                <v-divider dark class="my-1"></v-divider>

                <div class="font-weight-bold white--text">= {{ formatCurrency(Math.abs(getEffectiveValue(item.netValue))) }}</div>
              </div>
            </template>
            <template v-slot:item.customer="{ item }">{{ item.FIRSTNAME }} {{ item.LASTNAME }}</template>
            <template v-slot:body.append>
              <tr class="font-weight-bold" style="background-color: rgba(59, 130, 246, 0.1)">
                <td :colspan="detailsHeaders.length - 1" class="text-right white--text title">Total der gefilterten Einträge:</td>
                <td class="text-right white--text title pr-4">{{ formatCurrency(filteredTotalValue) }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showYoyDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card color="#111827">
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="showYoyDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>Umsatzentwicklung im Jahresvergleich</v-toolbar-title>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-card color="#1F2937" class="pa-4 mb-4 rounded-lg">
            <v-row align="center">
              <v-col cols="12" md="auto">
                <v-select
                  v-model="yoyYearsBack"
                  :items="[2, 3, 4, 5, 6, 7, 8, 9, 10, 11]"
                  label="Jahre zurück"
                  dark
                  dense
                  outlined
                  hide-details></v-select>
              </v-col>
              <v-col cols="12" md="5">
                <div class="d-flex align-center">
                  <v-autocomplete
                    v-if="yoyData"
                    v-model="yoySelectedStudios"
                    :items="Object.keys(yoyData.results)"
                    label="Studios auswählen"
                    dark
                    dense
                    outlined
                    hide-details
                    multiple
                    chips
                    deletable-chips
                    small-chips>
                    <template v-slot:selection="{ item, index }">
                      <v-chip small v-if="index < 2">
                        <span>{{ item }}</span>
                      </v-chip>
                      <span v-if="index === 2" class="grey--text text-caption ml-1"> (+{{ yoySelectedStudios.length - 2 }} weitere) </span>
                    </template>
                  </v-autocomplete>

                  <v-btn
                    icon
                    :color="areAllStudiosSelected ? 'red' : 'primary'"
                    class="ml-2"
                    @click="toggleSelectAllStudios"
                    :title="areAllStudiosSelected ? 'Auswahl aufheben' : 'Alle Studios auswählen'">
                    <v-icon>{{ areAllStudiosSelected ? "mdi-close-box-multiple" : "mdi-check-all" }}</v-icon>
                  </v-btn>
                </div>
              </v-col>

              <template v-if="yoyViewMode === 'custom'">
                <v-col cols="12" md="auto">
                  <v-select
                    v-model="compareYearA"
                    :items="availableYearsForComparison"
                    label="Jahr A"
                    dark
                    dense
                    outlined
                    hide-details
                    style="min-width: 150px"></v-select>
                </v-col>
                <v-col cols="12" md="auto">
                  <v-select
                    v-model="compareYearB"
                    :items="availableYearsForComparison.filter((y) => y !== compareYearA)"
                    label="Jahr B"
                    dark
                    dense
                    outlined
                    hide-details
                    style="min-width: 150px"></v-select>
                </v-col>
              </template>
              <v-col cols="12" md="auto" class="d-flex justify-end">
                <v-btn-toggle v-model="yoyViewMode" mandatory dense dark background-color="#111827">
                  <v-btn value="combined">
                    <v-icon left>mdi-chart-bar</v-icon>
                    Gesamt
                  </v-btn>
                  <v-btn value="split">
                    <v-icon left>mdi-chart-bar-stacked</v-icon>
                    Direktvergleich
                  </v-btn>
                  <v-btn value="custom">
                    <v-icon left>mdi-select-compare</v-icon>
                    Freier Vergleich
                  </v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>
          </v-card>

          <div v-if="yoyLoading" class="text-center py-16">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          </div>

          <div v-else-if="yoyComparisonData.years && yoyComparisonData.years.length > 0">
            <v-row v-if="yoyViewMode !== 'custom'">
              <v-col cols="12" sm="6" md="3">
                <v-card class="pa-3 rounded-lg kpi-card fill-height">
                  <div class="caption grey--text">YTD Umsatz vs Vorjahr</div>
                  <div class="h5 font-weight-bold white--text">{{ formatCurrency(yoyComparisonData.kpis.ytdCurrent) }}</div>
                  <div
                    class="d-flex align-center"
                    :class="yoyComparisonData.kpis.ytdGrowth >= 0 ? 'green--text text--accent-4' : 'red--text text--accent-3'">
                    <v-icon small class="mr-1">{{ yoyComparisonData.kpis.ytdGrowth >= 0 ? "mdi-arrow-top-right" : "mdi-arrow-bottom-left" }}</v-icon>
                    <span>{{ yoyComparisonData.kpis.ytdGrowth.toFixed(1) }}%</span>
                    <span class="caption grey--text ml-2">vs. {{ formatCurrency(yoyComparisonData.kpis.ytdPrevious) }}</span>
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card class="pa-3 rounded-lg kpi-card fill-height" hover @click="openRankDialog('revenue')">
                  <div class="caption grey--text">Bester Monat (All-Time)</div>
                  <div class="h5 font-weight-bold white--text">{{ yoyComparisonData.kpis.bestMonth }}</div>
                  <div class="body-2 white--text">{{ formatCurrency(yoyComparisonData.kpis.bestMonthRevenue) }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card class="pa-3 rounded-lg kpi-card fill-height" hover @click="openRankDialog('growth')">
                  <div class="caption grey--text">Stärkstes Monats-Wachstum</div>
                  <div class="h5 font-weight-bold white--text">{{ yoyComparisonData.kpis.bestGrowth }}</div>
                  <div class="body-2 green--text text--accent-4">+{{ yoyComparisonData.kpis.bestGrowthValue.toFixed(1) }}%</div>
                </v-card>
              </v-col>
            </v-row>

            <v-row v-if="yoyViewMode !== 'custom'">
              <v-col cols="12" lg="6">
                <v-card color="#1F2937" class="pa-4 mt-4 rounded-lg">
                  <v-card-title class="white--text pt-0 pl-0">Kumulierte Umsatzentwicklung</v-card-title>
                  <apexchart height="350" type="line" :series="cumulativeChartSeries" :options="cumulativeChartOptions"></apexchart>
                </v-card>
              </v-col>
              <v-col cols="12" lg="6">
                <v-card color="#1F2937" class="pa-4 mt-4 rounded-lg">
                  <v-card-title class="white--text pt-0 pl-0">Umsatz nach Quartal</v-card-title>
                  <apexchart height="350" type="bar" :series="quarterlyChartSeries" :options="quarterlyChartOptions"> </apexchart>
                </v-card>
              </v-col>
            </v-row>

            <v-divider dark class="my-4"></v-divider>
            <h2 class="white--text font-weight-light mb-4">Monatlicher Umsatzvergleich</h2>

            <div v-if="yoyViewMode === 'combined'">
              <v-card color="#1F2937" class="pa-4 rounded-lg">
                <apexchart height="400" type="line" :series="mainYoYChartSeries" :options="yoyChartOptions"></apexchart>
              </v-card>
            </div>

            <div v-if="yoyViewMode === 'split'">
              <v-row>
                <v-col v-for="(chart, index) in yoySplitChartData" :key="index" cols="12" xl="6">
                  <v-card color="#1F2937" class="pa-4 rounded-lg">
                    <v-card-title class="white--text pt-0 pl-0">{{ chart.title }}</v-card-title>
                    <apexchart height="400" type="bar" :series="chart.series" :options="chart.options"></apexchart>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </div>
          <div v-if="yoyViewMode === 'custom'">
            <v-card v-if="customComparisonChartData" color="#1F2937" class="pa-4 rounded-lg">
              <apexchart height="400" type="bar" :series="customComparisonChartData.series" :options="customComparisonChartData.options"></apexchart>
            </v-card>
            <div v-else class="text-center grey--text py-16">Bitte wählen Sie zwei Jahre für den Vergleich aus.</div>
          </div>
        </v-card-text>
      </v-card>
      <v-dialog dark v-model="showRankDialog" class="rounded-lg" max-width="600px">
        <v-card color="#111827 rounded-lg">
          <v-toolbar dark color="primary" dense>
            <v-toolbar-title>{{ rankDialogTitle }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="showRankDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="pt-4">
            <v-data-table
              :headers="rankDialogHeaders"
              :items="rankDialogItems"
              dark
              class="elevation-0 transparent"
              hide-default-footer
              disable-pagination
              dense>
              <template v-slot:item.revenue="{ item }">
                <span class="font-weight-bold">{{ formatCurrency(item.revenue) }}</span>
              </template>
              <template v-slot:item.growth="{ item }">
                <span :class="item.growth >= 0 ? 'green--text text--accent-4' : 'red--text text--accent-3'" class="font-weight-bold">
                  {{ item.growth > 0 ? "+" : "" }}{{ item.growth.toFixed(1) }}%
                </span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-dialog>
    <v-dialog v-model="showInvoiceDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <InvoiceAnalysis @refresh="fetchInvoicesStatistics" v-if="invoiceStats" :stats="invoiceStats" @close="showInvoiceDialog = false" />
    </v-dialog>

    <v-dialog v-model="showExpenseDialog" max-width="700px">
      <ExpenseCaptureForm
        :studios="studios"
        :invoice-issuers="invoiceIssuers"
        @close="showExpenseDialog = false"
        @expense-saved="handleExpenseSaved" />
    </v-dialog>

    <v-dialog v-model="showForecastImportDialog" max-width="680" persistent>
      <v-card color="#111827" dark>
        <v-card-title class="d-flex align-center">
          <v-icon color="#42b8f5" class="mr-3">mdi-database-import-outline</v-icon>
          Neuen TAC-Spiegel einlesen
        </v-card-title>
        <v-card-text>
          <v-alert color="blue-grey darken-3" text dense class="mb-5">
            Das Datum ist der tatsächliche Stand des Datenbank-Backups – nicht das heutige Datum und nicht die höchste zukünftige Fälligkeit. Es wird dauerhaft am Snapshot gespeichert.
          </v-alert>
          <v-row>
            <v-col cols="12" sm="7">
              <v-text-field
                v-model="forecastImportAsOfDate"
                type="date"
                label="Stand des TAC-Backups"
                outlined
                dense
                persistent-hint
                hint="Beispiel: Backup vom 29.06.2026 = 29.06.2026"></v-text-field>
            </v-col>
            <v-col cols="12" sm="5">
              <v-select
                v-model="forecastImportActualMonths"
                :items="forecastImportMonthOptions"
                item-text="text"
                item-value="value"
                label="V2-Isthistorie"
                outlined
                dense
                persistent-hint
                hint="für den V1/V2-Vergleich"></v-select>
            </v-col>
          </v-row>
          <v-checkbox
            v-model="forecastImportForce"
            dense
            hide-details
            label="Trotz identischer Quelldaten einen neuen Snapshot erstellen"></v-checkbox>
          <div class="caption grey--text mt-4">
            Importiert werden eine unveränderliche Ist-Tagesreihe, explizit erkannte Stornos, 365 Tage TAC-Fälligkeiten sowie Vertragsstarts und Verlängerungskandidaten mit Kundenhistorie. V1 wird dabei weder überschrieben noch umgeschaltet.
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn text :disabled="forecastImporting" @click="showForecastImportDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" :loading="forecastImporting" :disabled="!forecastImportAsOfDate" @click="startForecastImport">
            Import starten
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showForecastDetailsDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card color="#11151d" dark>
        <v-toolbar dark color="primary" fixed>
          <v-btn icon @click="showForecastDetailsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>
            Prognose-Einzelnachweis
            <span class="caption grey--text ml-2" v-if="forecastDetailsTotal">{{ formatNumber(forecastDetailsTotal) }} Zeilen</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-chip v-if="forecastSnapshot" small outlined>RADB {{ formatDate(forecastSnapshot.source.asOfDate) }}</v-chip>
        </v-toolbar>

        <v-card-text class="forecast-detail-body pt-5">
          <v-card class="forecast-panel pa-4 mb-4" elevation="0">
            <v-row dense>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="forecastDetailFilters.search"
                  label="Produkt oder TAC-ID suchen"
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  dense
                  clearable
                  hide-details
                  @keyup.enter="applyForecastDetailFilters"></v-text-field>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-select v-model="forecastDetailFilters.status" :items="forecastStatusOptions" label="Status" outlined dense hide-details></v-select>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-select v-model="forecastDetailFilters.kind" :items="forecastKindOptions" label="Art" outlined dense clearable hide-details></v-select>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-select v-model="forecastDetailFilters.confidence" :items="forecastConfidenceOptions" label="Preissicherheit" outlined dense clearable hide-details></v-select>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-select v-model="forecastDetailFilters.valueSource" :items="forecastValueSourceOptions" label="Preisquelle" outlined dense clearable hide-details></v-select>
              </v-col>
              <v-col cols="12" sm="5" md="3">
                <v-select v-model="forecastDetailFilters.category" :items="forecastCategoryOptions" label="Kategorie" outlined dense clearable hide-details></v-select>
              </v-col>
              <v-col cols="12" sm="5" md="4">
                <v-select v-model="forecastDetailFilters.promotion" :items="forecastPromotionOptions" label="Firmenrabatt / Spezialaktion" outlined dense clearable hide-details></v-select>
              </v-col>
              <v-col cols="12" sm="2" md="2" class="d-flex align-center">
                <v-btn color="primary" block @click="applyForecastDetailFilters">Anwenden</v-btn>
              </v-col>
              <v-col cols="12" md="3" class="d-flex align-center justify-end">
                <v-btn text color="grey lighten-1" @click="resetForecastDetailFilters">
                  <v-icon left>mdi-filter-remove-outline</v-icon>
                  Filter löschen
                </v-btn>
              </v-col>
            </v-row>
          </v-card>

          <v-data-table
            :headers="forecastDetailsHeaders"
            :items="forecastDetailsItems"
            :loading="forecastDetailsLoading"
            :server-items-length="forecastDetailsTotal"
            :options.sync="forecastDetailsOptions"
            :footer-props="{ 'items-per-page-options': [25, 50, 100, 200] }"
            item-key="_id"
            show-expand
            dark
            class="forecast-panel forecast-detail-table"
            @update:options="fetchForecastDetails">
            <template v-slot:item.dueDate="{ item }">
              <div class="font-weight-medium">{{ formatDate(item.dueDate) }}</div>
              <div class="caption grey--text">Payment {{ item.paymentId }}</div>
            </template>
            <template v-slot:item.studio="{ item }">
              <div>{{ item.studio ? item.studio.name : `TAC Studio ${item.studioTacId}` }}</div>
              <div v-if="item.studio && item.studio.code" class="caption grey--text">{{ item.studio.code }}</div>
            </template>
            <template v-slot:item.productName="{ item }">
              <div class="font-weight-medium white--text">{{ item.productName }}</div>
              <div class="caption grey--text text-truncate" style="max-width: 320px">{{ item.description }}</div>
            </template>
            <template v-slot:item.forecastKind="{ item }">
              <v-chip x-small outlined :color="forecastKindColor(item.forecastKind)">{{ forecastKindLabel(item.forecastKind) }}</v-chip>
            </template>
            <template v-slot:item.promotion="{ item }">
              <div v-if="item.promotion && item.promotion.name">
                <div class="amber--text text--lighten-1">{{ item.promotion.name }}</div>
                <div v-if="item.promotion.code" class="caption grey--text">{{ item.promotion.code }}</div>
              </div>
              <span v-else class="grey--text">–</span>
            </template>
            <template v-slot:item.confidence="{ item }">
              <div>{{ forecastSourceLabel(item.valueSource) }}</div>
              <div class="caption" :class="forecastConfidenceClass(item.confidence)">{{ forecastConfidenceLabel(item.confidence) }}</div>
            </template>
            <template v-slot:item.amount="{ item }">
              <span class="font-weight-bold" :class="item.amount < 0 ? 'amber--text text--lighten-1' : 'white--text'">
                {{ item.amount === null ? 'offen' : formatCurrency(item.amount) }}
              </span>
            </template>
            <template v-slot:item.isActive="{ item }">
              <v-chip x-small :color="item.isActive ? 'green darken-3' : 'red darken-3'">{{ item.isActive ? "aktiv" : "ausgeschlossen" }}</v-chip>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-0">
                <div class="forecast-evidence pa-5">
                  <v-row dense>
                    <v-col cols="12" md="3">
                      <div class="evidence-title">TAC-Gegenprüfung</div>
                      <dl class="evidence-list">
                        <div><dt>Kunde</dt><dd>{{ item.customerId || "–" }}</dd></div>
                        <div><dt>Vertrag</dt><dd>{{ item.contractId }}</dd></div>
                        <div><dt>Vertragscontainer</dt><dd>{{ item.contractContainerId || "–" }}</dd></div>
                        <div><dt>Payment</dt><dd>{{ item.paymentId }}</dd></div>
                        <div><dt>Kassa-Artikel</dt><dd>{{ item.kassaArticleId || "–" }}</dd></div>
                      </dl>
                      <v-btn x-small outlined color="primary" class="mt-3" @click="copyForecastReference(item)">
                        <v-icon left x-small>mdi-content-copy</v-icon>
                        Referenz kopieren
                      </v-btn>
                    </v-col>
                    <v-col cols="12" md="3">
                      <div class="evidence-title">Vertrag & Periode</div>
                      <dl class="evidence-list">
                        <div><dt>Vertragsstart</dt><dd>{{ formatDate(item.contract && item.contract.startDate) }}</dd></div>
                        <div><dt>Periodenstart</dt><dd>{{ formatDate(item.contract && item.contract.termStartDate) }}</dd></div>
                        <div><dt>Periodenende</dt><dd>{{ formatDate(item.contract && item.contract.termEndDate) }}</dd></div>
                        <div><dt>Sequenz</dt><dd>{{ item.contract && item.contract.termSequence !== null ? item.contract.termSequence : "–" }}</dd></div>
                        <div><dt>Auto-Verlängerung</dt><dd>{{ item.contract && item.contract.autoRenewal ? "Ja" : "Nein" }}</dd></div>
                      </dl>
                    </v-col>
                    <v-col cols="12" md="3">
                      <div class="evidence-title">Preisbeleg</div>
                      <dl class="evidence-list">
                        <div><dt>Verwendet</dt><dd>{{ forecastSourceLabel(item.valueSource) }}</dd></div>
                        <div><dt>Direkter Vertragswert</dt><dd>{{ formatOptionalCurrency(item.evidence && item.evidence.contractValue) }}</dd></div>
                        <div><dt>Aktuelle Definition</dt><dd>{{ formatOptionalCurrency(item.evidence && item.evidence.contractDefinitionValue) }}</dd></div>
                        <div><dt>Termwert / Raten</dt><dd>{{ formatOptionalCurrency(item.evidence && item.evidence.termGross) }} / {{ item.evidence && item.evidence.termPaymentRows || "–" }}</dd></div>
                        <div><dt>Vorheriger Artikel</dt><dd>{{ formatOptionalCurrency(item.evidence && item.evidence.previousPaymentValue) }}</dd></div>
                        <div><dt>Artikelpreis</dt><dd>{{ formatOptionalCurrency(item.evidence && item.evidence.articlePrice) }}</dd></div>
                      </dl>
                    </v-col>
                    <v-col cols="12" md="3">
                      <div class="evidence-title">Rabatt / Status</div>
                      <dl class="evidence-list">
                        <div><dt>Aktion</dt><dd>{{ item.promotion && item.promotion.name || "–" }}</dd></div>
                        <div><dt>Promotion-ID</dt><dd>{{ item.promotion && item.promotion.id || "–" }}</dd></div>
                        <div><dt>Firmen-/Mitgliedergruppe</dt><dd>{{ item.promotion && item.promotion.membershipGroupId || "–" }}</dd></div>
                        <div><dt>Rabattmodus</dt><dd>{{ promotionModeLabel(item.evidence && item.evidence.promotionMode) }}</dd></div>
                        <div><dt>Ausgeschlossen wegen</dt><dd>{{ formatExclusionReasons(item.exclusionReasons) }}</dd></div>
                      </dl>
                    </v-col>
                  </v-row>
                </div>
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showForecastBusinessDetailsDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card color="#11151d" dark>
        <v-toolbar dark color="primary" fixed>
          <v-btn icon @click="showForecastBusinessDetailsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>
            Vertragsprognose · Gegenprüfung
            <span class="caption ml-2" v-if="forecastBusinessDetailsTotal">{{ formatNumber(forecastBusinessDetailsTotal) }} Vertragsereignisse</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-chip v-if="forecastSnapshot" small outlined>RADB {{ formatDate(forecastSnapshot.source.asOfDate) }}</v-chip>
        </v-toolbar>

        <v-card-text class="forecast-detail-body pt-5">
          <v-alert text color="primary" dense class="mb-4">
            „Echter Neukunde“ wird nicht aus <code>IST_ERSTVERTRAG</code> übernommen: Entscheidend ist, ob beim gleichen TAC-Kunden ein älterer Vertrag gefunden wurde.
          </v-alert>
          <v-card class="forecast-panel pa-4 mb-4" elevation="0">
            <v-row dense>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="forecastBusinessDetailFilters.search"
                  label="Produkt, Kunde oder Vertrags-ID"
                  prepend-inner-icon="mdi-magnify"
                  outlined dense clearable hide-details
                  @keyup.enter="applyForecastBusinessDetailFilters"></v-text-field>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-select v-model="forecastBusinessDetailFilters.status" :items="forecastStatusOptions" label="Status" outlined dense hide-details></v-select>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-select v-model="forecastBusinessDetailFilters.kind" :items="forecastBusinessKindOptions" label="Quelle" outlined dense clearable hide-details></v-select>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-select v-model="forecastBusinessDetailFilters.classification" :items="forecastBusinessClassificationOptions" label="Ereignis" outlined dense clearable hide-details></v-select>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-select
                  v-model="forecastBusinessDetailFilters.scheduleStatus"
                  :items="forecastBusinessScheduleOptions"
                  :disabled="forecastBusinessDetailFilters.kind === 'known_contract_start'"
                  label="TAC-Abdeckung" outlined dense clearable hide-details></v-select>
              </v-col>
              <v-col cols="6" sm="4" md="2">
                <v-select v-model="forecastBusinessDetailFilters.categoryGroup" :items="forecastBusinessCategoryOptions" label="Kategorie" outlined dense clearable hide-details></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-btn color="primary" block @click="applyForecastBusinessDetailFilters">Anwenden</v-btn>
              </v-col>
              <v-col cols="12" sm="6" md="3" class="d-flex align-center">
                <v-btn text color="grey lighten-1" @click="resetForecastBusinessDetailFilters">
                  <v-icon left>mdi-filter-remove-outline</v-icon>Filter löschen
                </v-btn>
              </v-col>
            </v-row>
          </v-card>

          <v-data-table
            :headers="forecastBusinessDetailsHeaders"
            :items="forecastBusinessDetailsItems"
            :loading="forecastBusinessDetailsLoading"
            :server-items-length="forecastBusinessDetailsTotal"
            :options.sync="forecastBusinessDetailsOptions"
            :footer-props="{ 'items-per-page-options': [25, 50, 100, 200] }"
            item-key="_id"
            show-expand
            dark
            class="forecast-panel forecast-detail-table"
            @update:options="fetchForecastBusinessDetails">
            <template v-slot:item.projectedDate="{ item }">
              <div class="font-weight-medium">{{ formatDate(item.projectedDate) }}</div>
              <div class="caption grey--text">{{ projectionKindLabel(item.kind) }}</div>
            </template>
            <template v-slot:item.studio="{ item }">
              <div>{{ item.studio ? item.studio.name : `TAC Studio ${item.studioTacId}` }}</div>
              <div v-if="item.studio && item.studio.code" class="caption grey--text">{{ item.studio.code }}</div>
            </template>
            <template v-slot:item.classification="{ item }">
              <v-chip x-small outlined :color="item.contract && item.contract.terminateDate ? 'amber' : item.retentionTarget ? 'light-blue lighten-2' : projectionClassificationColor(item.classification)">
                {{ item.contract && item.contract.terminateDate ? "Rückholziel" : item.retentionTarget ? "Verlängerungsziel" : projectionClassificationLabel(item.classification) }}
              </v-chip>
            </template>
            <template v-slot:item.productName="{ item }">
              <div class="font-weight-medium white--text">
                {{ item.productName }}
                <v-chip v-if="item.resolvedPrice && item.resolvedPrice.isComplimentary" x-small outlined color="green accent-3" class="ml-2">Gratis</v-chip>
              </div>
              <div class="caption grey--text">{{ item.categoryGroup }} · {{ item.categorySubgroup }}</div>
            </template>
            <template v-slot:item.contractId="{ item }">
              <span class="font-weight-medium">{{ item.contractId }}</span>
            </template>
            <template v-slot:item.scheduleEvidence="{ item }">
              <div v-if="item.kind === 'renewal_candidate'">
                <v-chip
                  x-small
                  :color="item.resolvedPrice && item.resolvedPrice.isComplimentary && item.scheduleEvidence && item.scheduleEvidence.matched ? 'red darken-3' : item.scheduleEvidence && item.scheduleEvidence.matched ? 'green darken-3' : 'amber darken-3'">
                  {{ item.resolvedPrice && item.resolvedPrice.isComplimentary && item.scheduleEvidence && item.scheduleEvidence.matched ? "TAC prüfen" : item.scheduleEvidence && item.scheduleEvidence.matched ? "vorhanden" : "noch offen" }}
                </v-chip>
                <div v-if="item.scheduleEvidence && item.scheduleEvidence.paymentId" class="caption grey--text mt-1">Payment {{ item.scheduleEvidence.paymentId }}</div>
              </div>
              <span v-else class="grey--text">–</span>
            </template>
            <template v-slot:item.amount="{ item }">
              <div v-if="item.resolvedPrice && item.resolvedPrice.isComplimentary">
                <v-chip x-small dark color="green darken-1">Gratis</v-chip>
                <div class="caption green--text text--lighten-2 mt-1">keine Rechnung</div>
              </div>
              <div v-else>
                <div class="font-weight-bold">{{ item.resolvedPrice && item.resolvedPrice.amount ? formatCurrency(item.resolvedPrice.amount) : "offen" }}</div>
                <div class="caption grey--text">{{ projectionPriceSourceLabel(item.resolvedPrice && item.resolvedPrice.source) }}</div>
              </div>
            </template>
            <template v-slot:item.confidence="{ item }">
              <span :class="forecastConfidenceClass(item.resolvedPrice && item.resolvedPrice.confidence)">
                {{ forecastConfidenceLabel(item.resolvedPrice && item.resolvedPrice.confidence) }}
              </span>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-0">
                <div class="forecast-evidence pa-5">
                  <v-alert
                    v-if="item.resolvedPrice && item.resolvedPrice.isComplimentary && item.scheduleEvidence && item.scheduleEvidence.matched"
                    dense
                    text
                    type="warning"
                    class="mb-4">
                    Dieser Gratis-Vertrag besitzt bereits eine TAC-Fälligkeit (Payment {{ item.scheduleEvidence.paymentId }}). Die Prognose rechnet 0 CHF; die TAC-Fälligkeit sollte geprüft werden.
                  </v-alert>
                  <v-row dense>
                    <v-col cols="12" md="3">
                      <div class="evidence-title">Kundenhistorie</div>
                      <dl class="evidence-list">
                        <div><dt>Kunde</dt><dd>{{ item.customerId || "–" }}</dd></div>
                        <div><dt>Aktueller Vertrag</dt><dd>{{ item.contractId }}</dd></div>
                        <div><dt>Vorgängervertrag</dt><dd>{{ item.priorContract && item.priorContract.contractId || "keiner" }}</dd></div>
                        <div><dt>Abstand</dt><dd>{{ item.priorContract && item.priorContract.gapDays !== null ? `${item.priorContract.gapDays} Tage` : "–" }}</dd></div>
                        <div><dt>Einstufung</dt><dd>{{ item.contract && item.contract.terminateDate ? "Rückholziel" : item.retentionTarget ? "Verlängerungsziel" : projectionClassificationLabel(item.classification) }}</dd></div>
                      </dl>
                      <v-btn x-small outlined color="primary" class="mt-3" @click="copyProjectionReference(item)">
                        <v-icon left x-small>mdi-content-copy</v-icon>Referenz kopieren
                      </v-btn>
                    </v-col>
                    <v-col cols="12" md="3">
                      <div class="evidence-title">Vertrag</div>
                      <dl class="evidence-list">
                        <div><dt>Start</dt><dd>{{ formatDate(item.contract && item.contract.startDate) }}</dd></div>
                        <div><dt>Ende</dt><dd>{{ formatDate(item.contract && item.contract.endDate) }}</dd></div>
                        <div><dt>Kündigung</dt><dd>{{ formatDate(item.contract && item.contract.terminateDate) }}</dd></div>
                        <div><dt>Renewal bis</dt><dd>{{ formatDate(item.contract && item.contract.renewalUntil) }}</dd></div>
                        <div><dt>Auto-Renewal</dt><dd>{{ item.contract && item.contract.autoRenewal ? "Ja" : "Nein" }}</dd></div>
                        <div><dt>TAC-Fälligkeit</dt><dd>{{ item.scheduleEvidence && item.scheduleEvidence.matched ? formatDate(item.scheduleEvidence.dueDate) : "noch nicht erzeugt" }}</dd></div>
                      </dl>
                    </v-col>
                    <v-col cols="12" md="3">
                      <div class="evidence-title">Preis</div>
                      <dl class="evidence-list">
                        <div><dt>Verwendet</dt><dd>{{ projectionPriceSourceLabel(item.resolvedPrice && item.resolvedPrice.source) }}</dd></div>
                        <div><dt>Prognosewert</dt><dd>{{ projectionPriceDisplay(item) }}</dd></div>
                        <div v-if="item.resolvedPrice && item.resolvedPrice.isComplimentary"><dt>Referenzpreis</dt><dd>{{ item.resolvedPrice.referenceAmount === null ? "nicht verfügbar" : formatCurrency(item.resolvedPrice.referenceAmount) }}</dd></div>
                        <div><dt>Term-Brutto</dt><dd>{{ formatOptionalCurrency(item.priceEvidence && item.priceEvidence.termGross) }}</dd></div>
                        <div><dt>Letzte Zahlung</dt><dd>{{ formatOptionalCurrency(item.priceEvidence && item.priceEvidence.latestPaymentValue) }}</dd></div>
                        <div><dt>Payment-ID</dt><dd>{{ item.priceEvidence && item.priceEvidence.latestPaymentId || "–" }}</dd></div>
                      </dl>
                    </v-col>
                    <v-col cols="12" md="3">
                      <div class="evidence-title">Prüfgrenze</div>
                      <dl class="evidence-list">
                        <div><dt>IST_ERSTVERTRAG</dt><dd>nicht verwendet</dd></div>
                        <div><dt>Preis-Sicherheit</dt><dd>{{ forecastConfidenceLabel(item.resolvedPrice && item.resolvedPrice.confidence) }}</dd></div>
                        <div><dt>Status</dt><dd>{{ item.isActive ? "aktiv" : "ausgeschlossen" }}</dd></div>
                        <div><dt>Ausschluss</dt><dd>{{ formatExclusionReasons(item.exclusionReasons) }}</dd></div>
                      </dl>
                    </v-col>
                  </v-row>
                </div>
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showForecastComparisonDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card color="#0b1220" dark>
        <v-toolbar dark color="primary">
          <v-btn icon @click="showForecastComparisonDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>V1/V2-Doppelbetrieb</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-chip v-if="forecastComparison" small outlined color="white">
            gemeinsamer Stand bis {{ formatDate(forecastComparison.coverage.to) }}
          </v-chip>
        </v-toolbar>
        <v-card-text v-if="forecastComparison" class="pt-5">
          <v-alert v-if="forecastComparison.coverage.missingV1Series || forecastComparison.coverage.missingV2Series" color="amber darken-4" text dense>
            Nicht gepaarte Reihen werden bewusst nicht in die Differenz gerechnet: {{ forecastComparison.coverage.missingV1Series }} fehlen in V1,
            {{ forecastComparison.coverage.missingV2Series }} fehlen in V2.
          </v-alert>
          <v-row dense>
            <v-col cols="12" sm="6" lg="3" v-for="metric in comparisonMetrics" :key="metric.label">
              <v-card class="forecast-panel pa-4 fill-height" elevation="0">
                <div class="caption grey--text">{{ metric.label }}</div>
                <div class="text-h6 font-weight-bold" :class="metric.className">{{ metric.value }}</div>
                <div class="caption grey--text mt-1">{{ metric.hint }}</div>
              </v-card>
            </v-col>
          </v-row>
          <v-row dense class="mt-2">
            <v-col cols="12" lg="6">
              <v-card class="forecast-panel" elevation="0">
                <v-card-title class="subtitle-1">Abweichung nach Studio</v-card-title>
                <v-data-table :headers="forecastComparisonStudioHeaders" :items="forecastComparison.byStudio" dark dense hide-default-footer disable-pagination class="transparent">
                  <template v-slot:item.v1Total="{ item }">{{ formatCurrency(item.v1Total) }}</template>
                  <template v-slot:item.v2Total="{ item }">{{ formatCurrency(item.v2Total) }}</template>
                  <template v-slot:item.difference="{ item }"><span :class="differenceClass(item.difference)">{{ formatSignedCurrency(item.difference) }}</span></template>
                  <template v-slot:item.cancellationExcludedTotal="{ item }">{{ formatCurrency(item.cancellationExcludedTotal) }}</template>
                </v-data-table>
              </v-card>
            </v-col>
            <v-col cols="12" lg="6">
              <v-card class="forecast-panel" elevation="0">
                <v-card-title class="subtitle-1">Abweichung nach Tag</v-card-title>
                <v-data-table :headers="forecastComparisonDailyHeaders" :items="forecastComparison.daily" dark dense :items-per-page="15" class="transparent">
                  <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
                  <template v-slot:item.v1Total="{ item }">{{ formatCurrency(item.v1Total) }}</template>
                  <template v-slot:item.v2Total="{ item }">{{ formatCurrency(item.v2Total) }}</template>
                  <template v-slot:item.difference="{ item }"><span :class="differenceClass(item.difference)">{{ formatSignedCurrency(item.difference) }}</span></template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>

          <v-card class="forecast-panel mt-4" elevation="0">
            <div class="d-flex align-center justify-space-between flex-wrap pa-4 pb-2">
              <div class="mb-2">
                <div class="subtitle-1 font-weight-bold white--text">Exakte TAC-Storno-Nachweise</div>
                <div class="caption grey--text">
                  Original- und Gegenbuchung mit den IDs, die aus V2 entfernt wurden. {{ formatNumber(forecastCancellationsTotal) }} Nachweise in dieser Auswahl.
                </div>
              </div>
              <div class="d-flex align-center flex-wrap cancellation-filters">
                <v-text-field
                  v-model="forecastCancellationSearch"
                  label="ID, Text oder Grund suchen"
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  dense
                  clearable
                  hide-details
                  class="mr-2 mb-2"
                  style="min-width: 260px"
                  @keyup.enter="applyForecastCancellationFilters"></v-text-field>
                <v-select
                  v-model="forecastCancellationRole"
                  :items="forecastCancellationRoleOptions"
                  label="Buchungsrolle"
                  outlined
                  dense
                  hide-details
                  class="mr-2 mb-2"
                  style="min-width: 220px"
                  @change="applyForecastCancellationFilters"></v-select>
                <v-btn color="primary" class="mb-2" @click="applyForecastCancellationFilters">Anwenden</v-btn>
              </div>
            </div>
            <v-data-table
              :headers="forecastCancellationHeaders"
              :items="forecastCancellationsItems"
              :loading="forecastCancellationsLoading"
              :server-items-length="forecastCancellationsTotal"
              :options.sync="forecastCancellationsOptions"
              :footer-props="{ 'items-per-page-options': [25, 50, 100, 200] }"
              dark
              dense
              class="transparent"
              @update:options="fetchForecastCancellations">
              <template v-slot:item.date="{ item }">
                <div>{{ formatDate(item.date) }}</div>
                <div class="caption grey--text">Storno {{ formatDate(item.cancellation && item.cancellation.date) }}</div>
              </template>
              <template v-slot:item.studio="{ item }">{{ item.studio ? item.studio.name : `TAC Studio ${item.studioTacId}` }}</template>
              <template v-slot:item.role="{ item }">
                <v-chip x-small :color="item.role === 'original' ? 'primary' : 'amber darken-3'">
                  {{ item.role === "original" ? "Original" : "Gegenbuchung" }}
                </v-chip>
              </template>
              <template v-slot:item.description="{ item }">
                <div class="white--text">{{ item.description }}</div>
                <div class="caption grey--text">{{ item.accountName }}</div>
              </template>
              <template v-slot:item.transactionId="{ item }">
                <div class="font-weight-bold">{{ item.transactionId }}</div>
                <div v-if="item.parentTransactionId" class="caption grey--text">Parent {{ item.parentTransactionId }}</div>
              </template>
              <template v-slot:item.bills="{ item }">
                <div>O {{ item.originalBillId || "–" }}</div>
                <div class="caption amber--text text--lighten-1">S {{ item.cancellationBillId || "–" }}</div>
              </template>
              <template v-slot:item.absoluteValue="{ item }">
                <div class="font-weight-bold">{{ formatCurrency(item.absoluteValue) }}</div>
                <div class="caption grey--text">{{ item.value > 0 ? "+" : "" }}{{ item.value }}</div>
              </template>
              <template v-slot:item.cancellationReason="{ item }">
                <div>{{ item.cancellation && item.cancellation.reason || "–" }}</div>
                <div v-if="item.cancellation && item.cancellation.reasonId" class="caption grey--text">Grund-ID {{ item.cancellation.reasonId }}</div>
              </template>
            </v-data-table>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from "../../../api"
import InvoiceAnalysis from "./invoices/InvoiceAnalysis.vue"
import ExpenseCaptureForm from "./invoices/ExpenseCaptureForm.vue"
import RecurringExpenseManager from "./invoices/RecurringExpenseManager.vue" // Pfad anpassen
export default {
  name: "HomeDashboardTransactionStatistics",
  props: {
    datePickerRange: { type: Array, default: () => [] },
    studios: { type: Array, default: () => [] },
  },
  components: {
    InvoiceAnalysis,
    ExpenseCaptureForm,
    RecurringExpenseManager,
  },
  data() {
    return {
      // NEU: Für die MwSt Berechnung
      deductVAT: false,
      vatRate: 0.081, // 8.1%

      forecastState: null,
      forecastData: null,
      forecastBusinessData: null,
      forecastComparison: null,
      forecastLoading: false,
      forecastBusinessLoading: false,
      forecastBusinessError: "",
      forecastBusinessUpgradeRequired: false,
      forecastScenarioDeviation: 15,
      forecastScenarioDeviationOptions: [10, 15, 20],
      includeForecastInActuals: false,
      forecastComparisonLoading: false,
      forecastError: "",
      forecastPollTimer: null,
      forecastImporting: false,
      showForecastImportDialog: false,
      forecastImportAsOfDate: "",
      forecastImportActualMonths: 24,
      forecastImportForce: false,
      forecastImportMonthOptions: [
        { text: "12 Monate", value: 12 },
        { text: "24 Monate", value: 24 },
        { text: "36 Monate", value: 36 },
        { text: "60 Monate", value: 60 },
      ],
      showForecastDetailsDialog: false,
      showForecastBusinessDetailsDialog: false,
      showForecastComparisonDialog: false,
      forecastDetailRange: null,
      forecastDetailsLoading: false,
      forecastDetailsItems: [],
      forecastDetailsTotal: 0,
      forecastDetailsOptions: {
        page: 1,
        itemsPerPage: 50,
        sortBy: ["dueDate"],
        sortDesc: [false],
      },
      forecastDetailFilters: {
        search: "",
        status: "active",
        kind: null,
        confidence: null,
        valueSource: null,
        category: null,
        promotion: null,
      },
      forecastBusinessDetailRange: null,
      forecastBusinessDetailsLoading: false,
      forecastBusinessDetailsItems: [],
      forecastBusinessDetailsTotal: 0,
      forecastBusinessDetailsOptions: {
        page: 1,
        itemsPerPage: 50,
        sortBy: ["projectedDate"],
        sortDesc: [false],
      },
      forecastBusinessDetailFilters: {
        search: "",
        status: "active",
        kind: null,
        classification: null,
        scheduleStatus: null,
        categoryGroup: null,
      },
      forecastBusinessKindOptions: [
        { text: "Verlängerungskandidat", value: "renewal_candidate" },
        { text: "Bekannter Vertragsstart", value: "known_contract_start" },
      ],
      forecastBusinessClassificationOptions: [
        { text: "Verlängerung", value: "renewal" },
        { text: "Echter Neukunde", value: "new_member" },
        { text: "Reaktivierung", value: "reactivation" },
        { text: "Vertragswechsel", value: "replacement" },
        { text: "Zusatzvertrag", value: "additional_contract" },
      ],
      forecastBusinessScheduleOptions: [
        { text: "TAC-Fälligkeit vorhanden", value: "scheduled" },
        { text: "Noch ohne TAC-Fälligkeit", value: "unscheduled" },
      ],
      forecastBusinessDetailsHeaders: [
        { text: "Prognosedatum", value: "projectedDate", width: 140 },
        { text: "Studio", value: "studio", width: 160, sortable: false },
        { text: "Ereignis", value: "classification", width: 160 },
        { text: "Abo / Paket", value: "productName", minWidth: 260 },
        { text: "TAC Vertrag", value: "contractId", width: 130, align: "end", sortable: false },
        { text: "TAC-Fälligkeit", value: "scheduleEvidence", width: 145, sortable: false },
        { text: "Preis", value: "amount", width: 160, align: "end" },
        { text: "Sicherheit", value: "confidence", width: 120 },
        { text: "", value: "data-table-expand" },
      ],
      forecastStatusOptions: [
        { text: "Nur aktive", value: "active" },
        { text: "Nur ausgeschlossene", value: "excluded" },
        { text: "Alle", value: "all" },
      ],
      forecastKindOptions: [
        { text: "Verlängerung", value: "renewal" },
        { text: "Neuer Vertrag", value: "new_contract" },
        { text: "Laufende Rate", value: "installment" },
        { text: "Geplante Zahlung", value: "scheduled_payment" },
        { text: "Rabatt / Aktion", value: "discount" },
        { text: "Gebühr / Anpassung", value: "fee_adjustment" },
      ],
      forecastConfidenceOptions: [
        { text: "Hoch", value: "high" },
        { text: "Mittel", value: "medium" },
        { text: "Niedrig", value: "low" },
        { text: "Ungeklärt", value: "unresolved" },
      ],
      forecastProductHeaders: [
        { text: "Abo / Paket", value: "name" },
        { text: "Fälligkeiten", value: "paymentRows", align: "end" },
        { text: "Betrag", value: "amount", align: "end" },
      ],
      forecastPromotionHeaders: [
        { text: "Aktion", value: "name" },
        { text: "Verträge", value: "affectedContracts", align: "end" },
        { text: "Rabatt", value: "amount", align: "end" },
      ],
      forecastDetailsHeaders: [
        { text: "Fälligkeit", value: "dueDate", width: 125 },
        { text: "Studio", value: "studio", width: 150, sortable: false },
        { text: "Art", value: "forecastKind", width: 130 },
        { text: "Abo / Paket", value: "productName", minWidth: 260 },
        { text: "Aktion", value: "promotion", minWidth: 180, sortable: false },
        { text: "Preisquelle", value: "confidence", minWidth: 180 },
        { text: "Betrag", value: "amount", align: "end", width: 130 },
        { text: "Status", value: "isActive", width: 110, sortable: false },
        { text: "", value: "data-table-expand" },
      ],
      forecastComparisonStudioHeaders: [
        { text: "Studio", value: "studioName" },
        { text: "V1", value: "v1Total", align: "end" },
        { text: "V2", value: "v2Total", align: "end" },
        { text: "Differenz", value: "difference", align: "end" },
        { text: "Storno", value: "cancellationExcludedTotal", align: "end" },
      ],
      forecastComparisonDailyHeaders: [
        { text: "Tag", value: "date" },
        { text: "V1", value: "v1Total", align: "end" },
        { text: "V2", value: "v2Total", align: "end" },
        { text: "Differenz", value: "difference", align: "end" },
      ],
      forecastCancellationsLoading: false,
      forecastCancellationsItems: [],
      forecastCancellationsTotal: 0,
      forecastCancellationSearch: "",
      forecastCancellationRole: null,
      forecastCancellationRoleOptions: [
        { text: "Original + Gegenbuchung", value: null },
        { text: "Nur Originalbuchungen", value: "original" },
        { text: "Nur Gegenbuchungen", value: "reversal" },
      ],
      forecastCancellationsOptions: {
        page: 1,
        itemsPerPage: 50,
      },
      forecastCancellationHeaders: [
        { text: "Buchungsdatum", value: "date", width: 125 },
        { text: "Studio", value: "studio", width: 150, sortable: false },
        { text: "Rolle", value: "role", width: 120, sortable: false },
        { text: "Beschreibung / Konto", value: "description", minWidth: 260, sortable: false },
        { text: "TAC Transaction", value: "transactionId", width: 130, sortable: false },
        { text: "Original / Storno", value: "bills", width: 160, sortable: false },
        { text: "Betrag", value: "absoluteValue", align: "end", width: 120, sortable: false },
        { text: "Stornogrund", value: "cancellationReason", minWidth: 220, sortable: false },
      ],

      compareYearA: null,
      compareYearB: null,
      yoyViewMode: "combined",
      showRankDialog: false,
      rankDialogTitle: "",
      rankDialogHeaders: [],
      rankDialogItems: [],
      showRecurringManager: false,
      invoiceIssuers: [],
      invoiceStats: null,
      showInvoiceDialog: false,
      showExpenseDialog: false,
      showYoyDialog: false,
      yoyLoading: false,
      yoyData: null,
      yoyYearsBack: 3,
      yoySelectedStudios: ["Gesamt"],
      adjustmentsSummary: [],
      adjustmentsLoading: false,
      finishedLoading: false,
      statisticsError: "",
      statisticsRequestId: 0,
      data: null,
      actualV2Data: null,
      selectedStatisticsVersion: "v2",
      selectedDataType: "invoices",
      actualV2StudioHeaders: [
        { text: "Studio", value: "studioName" },
        { text: "Umsatz", value: "totalRevenue", align: "end" },
        { text: "Anzahl", value: "totalTransactions", align: "end" },
        { text: "Storno", value: "cancellationExcludedTotal", align: "end" },
      ],
      showDetailsDialog: false,
      detailsLoading: false,
      detailedTransactions: [],
      totalDetails: 0,
      paginationOptions: {
        page: 1,
        itemsPerPage: 5000,
      },
      detailsHeaders: [
        { text: "Datum", value: "BILLDATE", width: "15%" },
        { text: "Kunde", value: "customer", width: "15%" },
        { text: "Beschreibung", value: "DESCRIPTION", width: "25%" },
        { text: "Vertragsname", value: "ContractName", width: "20%" },
        { text: "Konto", value: "AccountName", width: "10%" },
        { text: "Betrag", value: "VALUE", align: "end", width: "15%" },
      ],
      filters: {
        hasAdjustment: false,
        customer: "",
        contractName: "",
        description: "",
        accountName: "",
      },
      chartColors: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#6B7280"],
      selectedMainCategory: "Abonnements",
      drilldownLevel: 0,
      drilldownHistory: [],
      drilldownItemData: null,
    }
  },
  async created() {
    this.invoiceIssuers = await api.invoiceIssuers.fetch()

    await Promise.all([this.fetchTransactionStatistics(), this.fetchInvoicesStatistics(), this.initializeForecastV2()])
  },
  beforeDestroy() {
    if (this.forecastPollTimer) clearTimeout(this.forecastPollTimer)
  },
  methods: {
    forecastPayload() {
      return {
        range: this.datePickerRange,
        studios: this.studios.map((studio) => studio._id),
      }
    },
    async initializeForecastV2() {
      this.forecastLoading = true
      this.forecastError = ""
      try {
        this.forecastState = await api.fetchTransactionSnapshotStateV2()
        if (this.forecastState.activeImport) this.scheduleForecastSnapshotPoll()
        const failedAt = this.forecastState.latestFailed?.completedAt ? new Date(this.forecastState.latestFailed.completedAt).getTime() : 0
        const readyAt = this.forecastState.latestReady?.completedAt ? new Date(this.forecastState.latestReady.completedAt).getTime() : 0
        if (failedAt > readyAt) {
          this.forecastError = this.forecastState.latestFailed.error?.message || "Der letzte Snapshot-Import ist fehlgeschlagen."
        }
        if (this.forecastState.latestReady && this.datePickerRange?.length === 2 && this.studios?.length) {
          await this.loadForecastDataV2()
        }
      } catch (error) {
        console.error("Fehler beim Laden des V2-Snapshot-Status:", error)
        this.forecastError =
          "Der RADB-Prognosestatus konnte nicht geladen werden. Die historische V2-Ist-Statistik wird unabhängig davon abgefragt."
      } finally {
        this.forecastLoading = false
      }
    },
    async loadForecastDataV2() {
      if (!(this.forecastState?.latestReady && this.datePickerRange?.length === 2 && this.studios?.length)) {
        this.forecastData = null
        this.forecastBusinessData = null
        this.forecastComparison = null
        return
      }

      const payload = this.forecastPayload()
      const forecastPromise = api
        .fetchTransactionForecastV2(payload)
        .then((data) => {
          this.forecastData = data
        })
        .catch((error) => {
          console.error("Fehler beim Laden der TAC-Prognose:", error)
          this.forecastError = error.response?.data?.message || "Die TAC-Prognose konnte nicht geladen werden."
        })
      const comparisonPromise = this.fetchForecastComparisonV2()
      const businessPromise = this.fetchForecastBusinessV2()
      await Promise.all([forecastPromise, businessPromise, comparisonPromise])
    },
    async refreshForecastV2() {
      if (!this.forecastState) return this.initializeForecastV2()
      this.forecastLoading = true
      this.forecastError = ""
      try {
        await this.loadForecastDataV2()
      } finally {
        this.forecastLoading = false
      }
    },
    async fetchForecastComparisonV2() {
      if (!(this.forecastState?.latestReady && this.datePickerRange?.length === 2 && this.studios?.length)) {
        this.forecastComparison = null
        return
      }
      this.forecastComparisonLoading = true
      try {
        this.forecastComparison = await api.fetchTransactionComparisonV2({
          ...this.forecastPayload(),
          dataType: this.selectedDataType,
        })
      } catch (error) {
        console.error("Fehler beim Laden des V1/V2-Vergleichs:", error)
        this.forecastComparison = null
      } finally {
        this.forecastComparisonLoading = false
      }
    },
    async fetchForecastBusinessV2() {
      if (!(this.forecastState?.latestReady && this.datePickerRange?.length === 2 && this.studios?.length)) {
        this.forecastBusinessData = null
        return
      }
      this.forecastBusinessLoading = true
      this.forecastBusinessError = ""
      this.forecastBusinessUpgradeRequired = false
      try {
        this.forecastBusinessData = await api.fetchTransactionBusinessForecastV2({
          ...this.forecastPayload(),
          deviationPercent: this.forecastScenarioDeviation,
        })
      } catch (error) {
        console.error("Fehler beim Laden der Vertragsprognose:", error)
        this.forecastBusinessData = null
        this.forecastBusinessUpgradeRequired = error.response?.data?.code === "SNAPSHOT_UPGRADE_REQUIRED"
        this.forecastBusinessError = error.response?.data?.message || "Die Vertragsprognose konnte nicht geladen werden."
      } finally {
        this.forecastBusinessLoading = false
      }
    },
    async updateForecastScenarioDeviation() {
      await this.fetchForecastBusinessV2()
    },
    openForecastComparison() {
      this.showForecastComparisonDialog = true
      this.forecastCancellationsOptions = { ...this.forecastCancellationsOptions, page: 1 }
      this.$nextTick(this.fetchForecastCancellations)
    },
    async fetchForecastCancellations() {
      if (!(this.showForecastComparisonDialog && this.forecastSnapshot && this.studios?.length)) return
      this.forecastCancellationsLoading = true
      try {
        const response = await api.fetchTransactionCancellationsV2({
          ...this.forecastPayload(),
          dataType: this.selectedDataType,
          role: this.forecastCancellationRole,
          search: this.forecastCancellationSearch,
          page: this.forecastCancellationsOptions.page,
          limit: this.forecastCancellationsOptions.itemsPerPage,
        })
        this.forecastCancellationsItems = response.items || []
        this.forecastCancellationsTotal = Number(response.total) || 0
      } catch (error) {
        console.error("Fehler beim Laden der Storno-Nachweise:", error)
        this.$toast.error(error.response?.data?.message || "Die Storno-Nachweise konnten nicht geladen werden.")
      } finally {
        this.forecastCancellationsLoading = false
      }
    },
    applyForecastCancellationFilters() {
      this.forecastCancellationsOptions = { ...this.forecastCancellationsOptions, page: 1 }
      this.$nextTick(this.fetchForecastCancellations)
    },
    openForecastImportDialog() {
      this.forecastImportAsOfDate = ""
      this.forecastImportForce = false
      this.showForecastImportDialog = true
    },
    async startForecastImport() {
      if (!this.forecastImportAsOfDate) return
      this.forecastImporting = true
      this.forecastError = ""
      try {
        const response = await api.importTransactionSnapshotV2({
          asOfDate: this.forecastImportAsOfDate,
          actualMonths: this.forecastImportActualMonths,
          force: this.forecastImportForce,
        })
        this.showForecastImportDialog = false
        if (response.started) {
          this.forecastState = { ...(this.forecastState || {}), activeImport: response.snapshot }
          this.$toast.success("Der TAC-Snapshot wurde gestartet. Die Seite aktualisiert sich automatisch.")
          this.scheduleForecastSnapshotPoll()
        } else {
          this.$toast.success("Dieser TAC-Datenstand ist bereits vollständig eingelesen.")
          await this.initializeForecastV2()
        }
      } catch (error) {
        const response = error.response?.data
        if (error.response?.status === 409 && response?.snapshot) {
          this.showForecastImportDialog = false
          this.forecastState = { ...(this.forecastState || {}), activeImport: response.snapshot }
          this.scheduleForecastSnapshotPoll()
        } else {
          this.forecastError = response?.message || "Der TAC-Snapshot konnte nicht gestartet werden."
          this.$toast.error(this.forecastError)
        }
      } finally {
        this.forecastImporting = false
      }
    },
    scheduleForecastSnapshotPoll() {
      if (this.forecastPollTimer) clearTimeout(this.forecastPollTimer)
      this.forecastPollTimer = setTimeout(this.pollForecastSnapshot, 5000)
    },
    async pollForecastSnapshot() {
      const previousReadyId = this.forecastState?.latestReady?.id
      try {
        const state = await api.fetchTransactionSnapshotStateV2()
        this.forecastState = state
        if (state.activeImport) {
          this.scheduleForecastSnapshotPoll()
          return
        }
        if (state.latestReady && state.latestReady.id !== previousReadyId) {
          this.$toast.success("Der neue TAC-Snapshot ist bereit.")
          await Promise.all([this.refreshForecastV2(), this.fetchTransactionStatistics()])
        } else if (state.latestFailed && state.latestFailed.completedAt) {
          this.forecastError = state.latestFailed.error?.message || "Der letzte Snapshot-Import ist fehlgeschlagen."
        }
      } catch (error) {
        console.error("Fehler beim Prüfen des Snapshot-Imports:", error)
        this.scheduleForecastSnapshotPoll()
      }
    },
    openForecastDetails(preset = {}) {
      this.forecastDetailRange = preset.range || [...this.datePickerRange]
      this.forecastDetailFilters = {
        search: preset.search || "",
        status: preset.status || "active",
        kind: preset.kind || null,
        confidence: preset.confidence || null,
        valueSource: preset.valueSource || null,
        category: preset.category || null,
        promotion: preset.promotion || null,
      }
      this.forecastDetailsOptions = { ...this.forecastDetailsOptions, page: 1 }
      this.showForecastDetailsDialog = true
      this.$nextTick(this.fetchForecastDetails)
    },
    openForecastMonth(month) {
      const [year, monthNumber] = month.split("-").map(Number)
      const end = new Date(Date.UTC(year, monthNumber, 0)).toISOString().slice(0, 10)
      this.openForecastDetails({ range: [`${month}-01`, end] })
    },
    openForecastProduct(item) {
      this.openForecastDetails({ search: item.name })
    },
    openForecastPromotion(item) {
      this.openForecastDetails({ promotion: item.name, kind: "discount" })
    },
    async fetchForecastDetails() {
      if (!(this.showForecastDetailsDialog && this.forecastSnapshot && this.studios?.length)) return
      this.forecastDetailsLoading = true
      try {
        const sortBy = this.forecastDetailsOptions.sortBy?.[0] || "dueDate"
        const response = await api.fetchTransactionForecastDetailsV2({
          range: this.forecastDetailRange || this.datePickerRange,
          studios: this.studios.map((studio) => studio._id),
          page: this.forecastDetailsOptions.page,
          limit: this.forecastDetailsOptions.itemsPerPage,
          sortBy,
          sortDirection: this.forecastDetailsOptions.sortDesc?.[0] ? "desc" : "asc",
          ...this.forecastDetailFilters,
        })
        this.forecastDetailsItems = response.items || []
        this.forecastDetailsTotal = Number(response.total) || 0
      } catch (error) {
        console.error("Fehler beim Laden der Prognose-Einzelnachweise:", error)
        this.$toast.error(error.response?.data?.message || "Die Prognose-Einzelnachweise konnten nicht geladen werden.")
      } finally {
        this.forecastDetailsLoading = false
      }
    },
    applyForecastDetailFilters() {
      this.forecastDetailsOptions = { ...this.forecastDetailsOptions, page: 1 }
      this.$nextTick(this.fetchForecastDetails)
    },
    resetForecastDetailFilters() {
      this.forecastDetailFilters = {
        search: "",
        status: "active",
        kind: null,
        confidence: null,
        valueSource: null,
        category: null,
        promotion: null,
      }
      this.forecastDetailRange = [...this.datePickerRange]
      this.applyForecastDetailFilters()
    },
    openForecastBusinessDetails(preset = {}) {
      this.forecastBusinessDetailRange = preset.range || [...this.datePickerRange]
      this.forecastBusinessDetailFilters = {
        search: preset.search || "",
        status: preset.status || "active",
        kind: preset.kind || null,
        classification: preset.classification || null,
        scheduleStatus: preset.scheduleStatus || null,
        categoryGroup: preset.categoryGroup || null,
      }
      this.forecastBusinessDetailsOptions = { ...this.forecastBusinessDetailsOptions, page: 1 }
      this.showForecastBusinessDetailsDialog = true
      this.$nextTick(this.fetchForecastBusinessDetails)
    },
    openForecastBusinessMonth(month, kind = null) {
      const [year, monthNumber] = month.split("-").map(Number)
      const end = new Date(Date.UTC(year, monthNumber, 0)).toISOString().slice(0, 10)
      this.openForecastBusinessDetails({ range: [`${month}-01`, end], kind })
    },
    async fetchForecastBusinessDetails() {
      if (!(this.showForecastBusinessDetailsDialog && this.forecastSnapshot && this.studios?.length)) return
      this.forecastBusinessDetailsLoading = true
      try {
        const sortBy = this.forecastBusinessDetailsOptions.sortBy?.[0] || "projectedDate"
        const response = await api.fetchTransactionBusinessForecastDetailsV2({
          range: this.forecastBusinessDetailRange || this.datePickerRange,
          studios: this.studios.map((studio) => studio._id),
          page: this.forecastBusinessDetailsOptions.page,
          limit: this.forecastBusinessDetailsOptions.itemsPerPage,
          sortBy,
          sortDirection: this.forecastBusinessDetailsOptions.sortDesc?.[0] ? "desc" : "asc",
          ...this.forecastBusinessDetailFilters,
        })
        this.forecastBusinessDetailsItems = response.items || []
        this.forecastBusinessDetailsTotal = Number(response.total) || 0
      } catch (error) {
        console.error("Fehler beim Laden der Vertragsprognose-Einzelnachweise:", error)
        this.$toast.error(error.response?.data?.message || "Die Vertragsprognose-Einzelnachweise konnten nicht geladen werden.")
      } finally {
        this.forecastBusinessDetailsLoading = false
      }
    },
    applyForecastBusinessDetailFilters() {
      if (this.forecastBusinessDetailFilters.kind === "known_contract_start") {
        this.forecastBusinessDetailFilters.scheduleStatus = null
      }
      this.forecastBusinessDetailsOptions = { ...this.forecastBusinessDetailsOptions, page: 1 }
      this.$nextTick(this.fetchForecastBusinessDetails)
    },
    resetForecastBusinessDetailFilters() {
      this.forecastBusinessDetailFilters = {
        search: "",
        status: "active",
        kind: null,
        classification: null,
        scheduleStatus: null,
        categoryGroup: null,
      }
      this.forecastBusinessDetailRange = [...this.datePickerRange]
      this.applyForecastBusinessDetailFilters()
    },
    forecastScenarioClass(key) {
      return `business-scenario business-scenario--${key}`
    },
    forecastScenarioHint(key) {
      return {
        cautious: `Bestand −${this.forecastScenarioDeviation}% · Wachstumsziel vorsichtiger`,
        plan: "100% Bestand sichern + Nettowachstumsziel",
        strong: `100% Bestand · Wachstumsziel ${this.forecastScenarioDeviation}% besser`,
      }[key]
    },
    forecastScenarioPartWidth(value, total) {
      const parsedTotal = Number(total) || 0
      if (parsedTotal <= 0) return "0%"
      return `${Math.max(0, Math.min(100, ((Number(value) || 0) / parsedTotal) * 100))}%`
    },
    projectionKindLabel(value) {
      return {
        renewal_candidate: "Verlängerungskandidat",
        known_contract_start: "Bekannter Vertragsstart",
      }[value] || value || "Unbekannt"
    },
    projectionClassificationLabel(value) {
      return {
        renewal: "Verlängerung",
        new_member: "Echter Neukunde",
        reactivation: "Reaktivierung",
        replacement: "Vertragswechsel",
        additional_contract: "Zusatzvertrag",
        unknown: "Ungeklärt",
      }[value] || value || "Unbekannt"
    },
    projectionClassificationColor(value) {
      return {
        renewal: "light-blue lighten-2",
        new_member: "green accent-3",
        reactivation: "blue lighten-2",
        replacement: "amber lighten-1",
        additional_contract: "purple lighten-2",
        unknown: "grey",
      }[value] || "grey"
    },
    projectionPriceSourceLabel(value) {
      return {
        term_gross: "TAC-Termwert",
        studio_category_average: "Studio-/Kategorie-Ø",
        category_average: "Kategorie-Ø",
        subscription_average: "Abo-Ø",
        complimentary_name: "Gratis · VIP/Mitarbeiter-Regel",
        unresolved: "Preis offen",
      }[value] || value || "Preis offen"
    },
    projectionPriceDisplay(item) {
      const resolvedPrice = item && item.resolvedPrice
      if (resolvedPrice && resolvedPrice.isComplimentary) return `Gratis (${this.formatCurrency(0)}) · keine Rechnung`
      return resolvedPrice && resolvedPrice.amount ? this.formatCurrency(resolvedPrice.amount) : "Preis offen"
    },
    forecastCoverageLabel(value) {
      return {
        covered: "TAC weitgehend erzeugt",
        partial: "teilweise erzeugt",
        gap: "TAC-Lücke sichtbar",
        no_candidates: "keine Kandidaten",
        outside_snapshot: "ausserhalb Spiegel",
      }[value] || value
    },
    forecastCoverageClass(value) {
      return {
        covered: "business-coverage--covered",
        partial: "business-coverage--partial",
        gap: "business-coverage--gap",
        no_candidates: "business-coverage--neutral",
        outside_snapshot: "business-coverage--outside",
      }[value] || "business-coverage--neutral"
    },
    formatForecastCount(value) {
      const parsed = Number(value) || 0
      return Number.isInteger(parsed) ? this.formatNumber(parsed) : parsed.toLocaleString("de-CH", { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    },
    formatSignedCount(value) {
      const parsed = Number(value) || 0
      return `${parsed > 0 ? "+" : ""}${this.formatForecastCount(parsed)}`
    },
    async copyProjectionReference(item) {
      const reference = `TAC Kunde ${item.customerId || "–"} | Vertrag ${item.contractId} | Vorgänger ${
        item.priorContract && item.priorContract.contractId || "–"
      } | ${this.projectionClassificationLabel(item.classification)} am ${this.formatDate(item.projectedDate)} | ${
        this.projectionPriceDisplay(item)
      }`
      try {
        await navigator.clipboard.writeText(reference)
        this.$toast.success("Vertragsreferenz kopiert.")
      } catch (error) {
        console.error("Vertragsreferenz konnte nicht kopiert werden:", error)
        this.$toast.error("Die Vertragsreferenz konnte nicht kopiert werden.")
      }
    },
    forecastKindLabel(value) {
      return {
        renewal: "Verlängerung",
        new_contract: "Neuer Vertrag",
        installment: "Laufende Rate",
        scheduled_payment: "Geplante Zahlung",
        discount: "Rabatt / Aktion",
        fee_adjustment: "Gebühr / Anpassung",
      }[value] || value || "Unbekannt"
    },
    forecastKindColor(value) {
      return {
        renewal: "green accent-3",
        new_contract: "light-blue lighten-2",
        installment: "blue lighten-2",
        scheduled_payment: "blue-grey lighten-2",
        discount: "amber lighten-1",
        fee_adjustment: "purple lighten-2",
      }[value] || "grey"
    },
    forecastSourceLabel(value) {
      return {
        contract_value: "Direkter Vertragswert",
        promotion: "Berechnete Aktion",
        contract_definition_renewal: "Aktuelle Vertragsdefinition",
        term_total_divided: "Termwert aufgeteilt",
        article_price: "Artikelpreis",
        previous_exact_article: "Letzter gleicher Artikel",
        unresolved: "Noch ungeklärt",
      }[value] || value || "Unbekannt"
    },
    forecastConfidenceLabel(value) {
      return { high: "hohe Sicherheit", medium: "mittlere Sicherheit", low: "niedrige Sicherheit", unresolved: "ungeklärt" }[value] || value
    },
    forecastConfidenceClass(value) {
      return { high: "green--text text--accent-3", medium: "blue--text text--lighten-2", low: "amber--text", unresolved: "red--text" }[value]
    },
    promotionModeLabel(value) {
      return {
        periodic_percentage: "Periodisch in Prozent",
        periodic_value: "Periodischer Fixbetrag",
        once_percentage: "Einmalig in Prozent",
        once_value: "Einmaliger Fixbetrag",
      }[value] || "–"
    },
    formatExclusionReasons(reasons) {
      if (!Array.isArray(reasons) || !reasons.length) return "nicht ausgeschlossen"
      const labels = {
        payment_disabled: "Fälligkeit deaktiviert",
        contract_deleted: "Vertrag gelöscht",
        terminated_before_due: "vor Fälligkeit gekündigt",
        contract_ended_before_due: "Vertrag vorher beendet",
      }
      return reasons.map((reason) => labels[reason] || reason).join(", ")
    },
    formatDate(value) {
      if (!value) return "–"
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return "–"
      return date.toLocaleDateString("de-CH", { timeZone: "UTC", day: "2-digit", month: "2-digit", year: "numeric" })
    },
    formatForecastMonth(value) {
      const [year, month] = String(value).split("-").map(Number)
      if (!year || !month) return value
      return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("de-CH", { timeZone: "UTC", month: "short", year: "numeric" })
    },
    formatNumber(value) {
      return (Number(value) || 0).toLocaleString("de-CH")
    },
    formatSignedCurrency(value) {
      const amount = Number(value) || 0
      return `${amount > 0 ? "+" : ""}${this.formatCurrency(amount)}`
    },
    formatOptionalCurrency(value) {
      if (value === null || value === undefined || value === "") return "–"
      return this.formatCurrency(Number(value))
    },
    forecastMonthWidth(value) {
      if (!this.forecastMonthMax) return "0%"
      return `${Math.max(2, Math.min(100, (Math.abs(Number(value) || 0) / this.forecastMonthMax) * 100))}%`
    },
    differenceClass(value) {
      const amount = Number(value) || 0
      if (Math.abs(amount) < 0.01) return "green--text text--accent-3"
      return amount > 0 ? "forecast-accent-text" : "red--text text--lighten-2"
    },
    async copyForecastReference(item) {
      const reference = `TAC Kunde ${item.customerId || "–"} | Vertrag ${item.contractId} | Container ${item.contractContainerId || "–"} | Payment ${item.paymentId} | Fällig ${this.formatDate(
        item.dueDate,
      )} | ${item.amount === null ? "Preis offen" : this.formatCurrency(item.amount)}`
      try {
        await navigator.clipboard.writeText(reference)
        this.$toast.success("TAC-Referenz kopiert.")
      } catch (error) {
        console.error("TAC-Referenz konnte nicht kopiert werden:", error)
        this.$toast.error("Die TAC-Referenz konnte nicht kopiert werden.")
      }
    },
    // NEU: Zentrale Methode zur Berechnung des Netto-Werts
    getEffectiveValue(value) {
      if (!value) return 0
      // Wenn Switch AN ist UND wir bei Zahlungen sind, MwSt abziehen
      if (this.deductVAT && this.selectedDataType === "payments") {
        return value / (1 + this.vatRate)
      }
      return value
    },
    // NEU: Hilfsfunktion für die Anzeige der abgezogenen Steuer
    getVatAmount(value) {
      if (!value) return 0
      const net = value / (1 + this.vatRate)
      return value - net
    },

    toggleSelectAllStudios() {
      if (this.areAllStudiosSelected) {
        this.yoySelectedStudios = ["Gesamt"]
      } else {
        this.yoySelectedStudios = this.allStudioNames
      }
    },
    openRankDialog(type) {
      const months = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"]

      const allMonths = this.yoyComparisonData.years.flatMap((year) =>
        year.monthlyDetails.map((month, index) => ({
          name: `${months[index]} ${year.year}`,
          revenue: month.revenue,
          growth: month.growth,
          year: year.year,
        })),
      )

      switch (type) {
        case "revenue":
          this.rankDialogTitle = "Monate sortiert nach Umsatz"
          this.rankDialogHeaders = [
            { text: "Rang", value: "rank", sortable: false },
            { text: "Monat", value: "name", sortable: false },
            { text: "Umsatz", value: "revenue", align: "end", sortable: false },
          ]
          this.rankDialogItems = allMonths.sort((a, b) => b.revenue - a.revenue).map((item, index) => ({ ...item, rank: index + 1 }))
          break

        case "growth":
          this.rankDialogTitle = "Monate sortiert nach Wachstum (YoY)"
          this.rankDialogHeaders = [
            { text: "Rang", value: "rank", sortable: false },
            { text: "Monat", value: "name", sortable: false },
            { text: "YoY Wachstum", value: "growth", align: "end", sortable: false },
          ]
          const oldestYear = [...this.yoyData.years].sort((a, b) => a - b)[0]
          this.rankDialogItems = allMonths
            .filter((m) => m.year > oldestYear)
            .sort((a, b) => b.growth - a.growth)
            .map((item, index) => ({ ...item, rank: index + 1 }))
          break
      }

      this.showRankDialog = true
    },
    async fetchInvoicesStatistics() {
      if (!(this.datePickerRange?.length === 2 && this.studios?.length > 0)) return

      try {
        const params = {
          startDate: this.datePickerRange[0],
          endDate: this.datePickerRange[1],
          studio: this.studios.map((s) => s._id),
        }
        this.invoiceStats = await api.documents.fetchInvoiceStatistics(params)
      } catch (error) {
        console.error("Fehler beim Abrufen der Rechnungs-Statistiken:", error)
        this.$toast.error("Fehler beim Abrufen der Rechnungs-Statistiken")
      }
    },

    handleExpenseSaved() {
      this.showExpenseDialog = false
      this.$toast.success("Ausgabe erfolgreich erfasst!")
      this.fetchInvoicesStatistics()
    },
    openYoyDialog() {
      this.showYoyDialog = true
      this.fetchYoyData()
    },
    async fetchYoyData() {
      this.yoyLoading = true
      try {
        this.yoyData = await api.fetchYoyComparison({
          range: this.datePickerRange,
          studios: this.studios.map((s) => s._id),
          years_back: this.yoyYearsBack,
          dataType: this.selectedDataType,
        })
        if (!this.yoyData.results[this.yoySelectedStudio]) {
          this.yoySelectedStudio = "Gesamt"
        }
      } catch (error) {
        console.error("Fehler beim Laden der YoY-Daten:", error)
      } finally {
        this.yoyLoading = false
      }
    },
    async fetchTransactionStatistics() {
      const requestId = ++this.statisticsRequestId
      if (!(this.datePickerRange?.length === 2 && this.studios?.length > 0)) {
        this.data = null
        this.actualV2Data = null
        this.finishedLoading = true
        return
      }

        this.finishedLoading = false
        this.adjustmentsLoading = true
        this.statisticsError = ""

        try {
          const requestedVersion = this.selectedStatisticsVersion
          const payload = {
            range: this.datePickerRange,
            studios: this.studios.map((s) => s._id),
            dataType: this.selectedDataType,
          }
          const statsRequest = requestedVersion === "v2" ? api.fetchTransactionActualsV2(payload) : api.fetchTransactionStatistics(payload)
          const [statsResult, adjustmentsResult] = await Promise.all([
            statsRequest.then((value) => ({ value })).catch((error) => ({ error })),
            api
              .fetchAdjustmentsSummary(payload)
              .then((value) => ({ value }))
              .catch((error) => ({ error })),
          ])
          if (requestId !== this.statisticsRequestId) return

          if (statsResult.error) {
            console.error("Fehler beim Laden der Statistiken:", statsResult.error)
            this.data = null
            this.actualV2Data = null
            const statusCode = statsResult.error.response?.status
            const statusSuffix = statusCode ? ` (HTTP ${statusCode})` : ""
            this.statisticsError = `${
              statsResult.error.response?.data?.message ||
              (requestedVersion === "v2" ? "Die bereinigte V2-Ist-Statistik konnte nicht geladen werden." : "Die V1-Statistik konnte nicht geladen werden.")
            }${statusSuffix}`
          } else if (requestedVersion === "v2") {
            this.actualV2Data = statsResult.value
            const summary = statsResult.value?.summary || {}
            const previousYear = statsResult.value?.previousYear
            this.data = statsResult.value?.coverage?.valid
              ? {
                  totalRevenue: Number(summary.totalRevenue) || 0,
                  totalTransactions: Number(summary.totalTransactions) || 0,
                  previousYearTotalRevenue:
                    statsResult.value?.coverage?.complete && previousYear?.coverage?.complete ? Number(previousYear.summary?.totalRevenue) || 0 : 0,
                  revenueOverTime: (statsResult.value?.daily || []).map((day) => ({ date: day.date, total: Number(day.totalRevenue) || 0 })),
                  revenueBySubscription: [],
                  revenueByCategory: [],
                  revenueByGender: null,
                  uniqueCustomerCount: 0,
                }
              : null
          } else {
            this.actualV2Data = null
            this.data = statsResult.value
          }

          if (adjustmentsResult.error) {
            console.error("Fehler beim Laden der Promotionsübersicht:", adjustmentsResult.error)
            this.adjustmentsSummary = []
          } else {
            this.adjustmentsSummary = adjustmentsResult.value || []
          }
        } catch (error) {
          console.error("Unerwarteter Fehler beim Laden der Statistiken:", error)
          this.data = null
          this.actualV2Data = null
          this.statisticsError = "Die Statistik konnte nicht geladen werden."
        } finally {
          if (requestId === this.statisticsRequestId) {
            this.finishedLoading = true
            this.adjustmentsLoading = false
          }
        }
    },
    async fetchTransactionDetails() {
      this.detailsLoading = true
      const response = await api.fetchTransactionDetails({
        range: this.datePickerRange,
        studios: this.studios.map((s) => s._id),
        dataType: this.selectedDataType,
        options: this.paginationOptions,
      })
      this.detailedTransactions = response.items
      this.totalDetails = Number(response.total)
      this.detailsLoading = false
    },
    async fetchDrilldownData(mainCat, subCat) {
      this.drilldownItemData = null
      this.drilldownItemData = await api.fetchTransactionStatisticsDrill({
        range: this.datePickerRange,
        studios: this.studios.map((s) => s._id),
        mainCategory: mainCat,
        subCategory: subCat,
        dataType: this.selectedDataType,
      })
    },
    handleChartClick(event, chartContext, config) {
      if (this.drilldownLevel === 0) {
        const subCategoryName = config.w.globals.labels[config.dataPointIndex]
        this.drilldownHistory = [this.selectedMainCategory, subCategoryName]
        this.drilldownLevel = 1
        this.fetchDrilldownData(this.selectedMainCategory, subCategoryName)
      }
    },
    drillUp() {
      this.drilldownLevel = 0
      this.drilldownHistory = []
      this.drilldownItemData = null
    },
    formatCurrency(value) {
      if (typeof value !== "number") {
        return "0.00 CHF"
      }
      const formattedNumber = value.toLocaleString("de-CH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      return `${formattedNumber} CHF`
    },
  },
  computed: {
    forecastSnapshot() {
      return this.forecastData?.snapshot || this.actualV2Data?.snapshot || this.forecastState?.latestReady || null
    },
    forecastStartsAt() {
      const explicit = this.forecastBusinessData?.dataQuality?.forecastStartsAt || this.forecastBusinessData?.forecastRange?.from
      if (explicit) return explicit
      const projectionStart = this.forecastSnapshot?.contractProjectionCoverage?.from
      if (projectionStart) return projectionStart
      const asOfDate = this.forecastSnapshot?.source?.asOfDate
      if (!asOfDate) return null
      const nextDay = new Date(asOfDate)
      if (Number.isNaN(nextDay.getTime())) return null
      nextDay.setUTCDate(nextDay.getUTCDate() + 1)
      return nextDay
    },
    forecastRangeMode() {
      if (this.forecastBusinessData?.rangeMode) return this.forecastBusinessData.rangeMode
      if (!(this.forecastStartsAt && this.datePickerRange?.length === 2)) return "forecast"
      const start = new Date(this.datePickerRange[0]).getTime()
      const end = new Date(this.datePickerRange[1]).getTime()
      const forecastStart = new Date(this.forecastStartsAt).getTime()
      if ([start, end, forecastStart].some(Number.isNaN)) return "forecast"
      if (end < forecastStart) return "historical"
      return start < forecastStart ? "mixed" : "forecast"
    },
    forecastRangeIsHistorical() {
      return this.forecastRangeMode === "historical"
    },
    forecastRangeIsMixed() {
      return this.forecastRangeMode === "mixed"
    },
    forecastPlanScenario() {
      return (this.forecastBusinessData?.scenarios || []).find((scenario) => scenario.key === "plan") || null
    },
    forecastMixAvailable() {
      return Boolean(
        this.selectedStatisticsVersion === "v2" &&
          !this.forecastRangeIsHistorical &&
          this.forecastBusinessData?.forecastRange?.from &&
          this.forecastPlanScenario,
      )
    },
    forecastMixActive() {
      return this.includeForecastInActuals && this.forecastMixAvailable
    },
    actualRevenueRaw() {
      return Number(this.data?.totalRevenue) || 0
    },
    forecastRevenueRaw() {
      return this.forecastMixActive ? Number(this.forecastPlanScenario?.amount) || 0 : 0
    },
    displayActualRevenue() {
      return this.getEffectiveValue(this.actualRevenueRaw)
    },
    displayForecastRevenue() {
      return this.getEffectiveValue(this.forecastRevenueRaw)
    },
    displayRevenue() {
      return this.displayActualRevenue + this.displayForecastRevenue
    },
    hasDisplayedRevenue() {
      return Boolean(this.data) || this.forecastMixActive
    },
    revenueMixActualPercent() {
      const actual = Math.max(0, this.displayActualRevenue)
      const forecast = Math.max(0, this.displayForecastRevenue)
      return actual + forecast > 0 ? (actual / (actual + forecast)) * 100 : 0
    },
    revenueMixForecastPercent() {
      return 100 - this.revenueMixActualPercent
    },
    forecastHeaderTitle() {
      if (this.forecastRangeIsHistorical) return "Dieser Zeitraum ist bereits abgeschlossen"
      if (this.forecastRangeIsMixed) return "Ist-Zeitraum und Prognose sauber getrennt"
      return "Was wird im gewählten Zeitraum fällig?"
    },
    forecastHeaderSubtitle() {
      if (this.forecastRangeIsHistorical) return "Hier zählt das bereinigte Ist."
      if (this.forecastRangeIsMixed) return "Ist bis Spiegelstand · Prognose ab Folgetag."
      return "Fälligkeiten, Bestand und Wachstumsziel."
    },
    hasConfirmedForecastRows() {
      return Number(this.forecastData?.summary?.paymentRows) > 0
    },
    forecastImportProgress() {
      const progress = this.forecastState?.activeImport?.progress
      if (!progress?.totalChunks) return 5
      return Math.max(5, Math.min(98, (Number(progress.completedChunks || 0) / Number(progress.totalChunks)) * 100))
    },
    forecastHighConfidencePercent() {
      const summary = this.forecastData?.summary
      if (!summary?.paymentRows) return 0
      return (Number(summary.highConfidenceRows || 0) / Number(summary.paymentRows)) * 100
    },
    forecastMonthMax() {
      return Math.max(0, ...(this.forecastData?.monthly || []).map((month) => Math.abs(Number(month.amount) || 0)))
    },
    forecastValueSourceOptions() {
      return (this.forecastData?.valueSources || []).map((source) => ({ text: this.forecastSourceLabel(source.name), value: source.name }))
    },
    forecastCategoryOptions() {
      return [...new Set((this.forecastData?.categories || []).map((category) => category.main).filter(Boolean))].sort()
    },
    forecastPromotionOptions() {
      return [...new Set((this.forecastData?.promotions || []).map((promotion) => promotion.name).filter(Boolean))].sort()
    },
    forecastBusinessCategoryOptions() {
      return [...new Set((this.forecastBusinessData?.productCoverage || []).map((product) => product.categoryGroup).filter(Boolean))].sort()
    },
    comparisonDifferenceClass() {
      return this.differenceClass(this.forecastComparison?.summary?.difference)
    },
    comparisonMetrics() {
      const comparison = this.forecastComparison
      if (!comparison) return []
      return [
        {
          label: "V1-Summe",
          value: this.formatCurrency(comparison.summary.v1Total),
          hint: `${this.formatNumber(comparison.summary.v1Transactions)} Transaktionen`,
          className: "white--text",
        },
        {
          label: "V2 bereinigt",
          value: this.formatCurrency(comparison.summary.v2Total),
          hint: `${this.formatNumber(comparison.summary.v2Transactions)} Transaktionen`,
          className: "forecast-accent-text",
        },
        {
          label: "V2 minus V1",
          value: this.formatSignedCurrency(comparison.summary.difference),
          hint: comparison.summary.differencePercent === null ? "kein belastbarer Prozentwert" : `${comparison.summary.differencePercent.toFixed(2)}%`,
          className: this.differenceClass(comparison.summary.difference),
        },
        {
          label: "Erkannte Storno-Wirkung",
          value: this.formatCurrency(comparison.summary.cancellationExcludedTotal),
          hint: `${this.formatNumber(comparison.summary.cancellationExcludedCount)} ausgeschlossene Zeilen`,
          className: "amber--text text--lighten-1",
        },
      ]
    },
    comparisonToPreviousYear() {
      if (!this.data || !this.data.previousYearTotalRevenue) {
        return { percentage: null, trend: "neutral" }
      }
      if (this.data.previousYearTotalRevenue === 0) {
        return { percentage: Infinity, trend: "up" }
      }

      // Wir nehmen hier bewusst die Rohdaten für den %-Vergleich, da der Prozentsatz bei Brutto/Netto identisch ist
      // Aber für die Anzeige müssen wir Effective Value nutzen (wird im Template gemacht)
      const current = this.data.totalRevenue
      const previous = this.data.previousYearTotalRevenue
      const percentage = ((current - previous) / previous) * 100

      let trend = "neutral"
      if (percentage > 0) trend = "up"
      if (percentage < 0) trend = "down"

      return { percentage, trend }
    },
    allStudioNames() {
      if (!this.yoyData) return []
      return Object.keys(this.yoyData.results).filter((name) => name !== "Gesamt")
    },
    areAllStudiosSelected() {
      return this.yoySelectedStudios.length === this.allStudioNames.length
    },
    availableYearsForComparison() {
      if (!this.yoyComparisonData.years) return []
      return this.yoyComparisonData.years.map((y) => y.year).sort((a, b) => b - a)
    },
    aggregatedStudioDataForYoY() {
      if (!this.yoyData || this.yoySelectedStudios.length === 0) {
        return null
      }

      const aggregatedData = {}
      const allYears = this.yoyData.years

      allYears.forEach((year) => {
        aggregatedData[year] = Array(12).fill(0)
      })

      this.yoySelectedStudios.forEach((studioName) => {
        const studioData = this.yoyData.results[studioName]
        if (studioData) {
          for (const year in studioData) {
            if (aggregatedData[year]) {
              for (let i = 0; i < 12; i++) {
                aggregatedData[year][i] += studioData[year][i] || 0
              }
            }
          }
        }
      })

      return aggregatedData
    },
    yoyChartTitle() {
      switch (this.yoyViewMode) {
        case "combined":
          return "Monatlicher Umsatzvergleich (Gesamtübersicht)"
        case "split":
          return "Monatlicher Umsatzvergleich (Jahr vs. Vorjahr)"
        case "custom":
          return `Freier Vergleich: ${this.compareYearA || ""} vs. ${this.compareYearB || ""}`
        default:
          return "Monatlicher Umsatzvergleich"
      }
    },
    customComparisonChartData() {
      if (!this.compareYearA || !this.compareYearB || !this.yoyComparisonData.years) return null

      const yearA_data = this.yoyComparisonData.years.find((y) => y.year === this.compareYearA)
      const yearB_data = this.yoyComparisonData.years.find((y) => y.year === this.compareYearB)

      if (!yearA_data || !yearB_data) return null

      return {
        series: [
          { name: yearA_data.year.toString(), data: yearA_data.monthlyDetails.map((m) => m.revenue) },
          { name: yearB_data.year.toString(), data: yearB_data.monthlyDetails.map((m) => m.revenue) },
        ],
        options: {
          ...this.yoyChartOptions,
          dataLabels: { enabled: false },
          tooltip: {
            theme: "dark",
            shared: true,
            intersect: false,
            y: {
              formatter: (val, { seriesIndex, dataPointIndex, w }) => {
                const seriesName = w.globals.seriesNames[seriesIndex]
                const valueA = yearA_data.monthlyDetails[dataPointIndex].revenue
                const valueB = yearB_data.monthlyDetails[dataPointIndex].revenue

                if (valueA > 0) {
                  const growth = ((valueB - valueA) / valueA) * 100
                  const sign = growth > 0 ? "+" : ""
                  const color = growth > 0 ? "#10B981" : "#EF4444"

                  if (seriesName == yearB_data.year.toString()) {
                    return `${this.formatCurrency(val)} <span style="font-weight: 600; color: ${color};">(${sign}${growth.toFixed(1)}% vs ${
                      yearA_data.year
                    })</span>`
                  }
                }
                return this.formatCurrency(val)
              },
            },
          },
        },
      }
    },
    invoiceTotal() {
      return this.invoiceStats?.grandTotal?.totalAmount || 0
    },
    estimatedProfit() {
      return this.displayRevenue - this.invoiceTotal
    },
    adjustmentsTotal() {
      if (!this.adjustmentsSummary) return { totalValue: 0, count: 0 }
      return this.adjustmentsSummary.reduce(
        (acc, item) => {
          acc.totalValue += item.totalValue
          acc.count += item.count
          return acc
        },
        { totalValue: 0, count: 0 },
      )
    },
    allItems() {
      if (!this.data) return []
      const subscriptions = this.data.revenueBySubscription || []
      const others = this.data.revenueByCategory || []
      const subItems = subscriptions.flatMap((s) => s.items || [])
      const otherItems = others.flatMap((c) => c.items || [])

      const combined = [...subItems, ...otherItems].reduce((acc, item) => {
        if (!acc[item.name]) {
          acc[item.name] = { name: item.name, total: 0, count: 0 }
        }
        acc[item.name].total += item.total
        acc[item.name].count += item.count
        return acc
      }, {})

      return Object.values(combined)
    },
    top5ByRevenue() {
      return this.allItems.sort((a, b) => b.total - a.total).slice(0, 5)
    },
    top5ByCount() {
      return this.allItems.sort((a, b) => b.count - a.count).slice(0, 5)
    },
    avgRevenuePerCustomer() {
      if (!this.data || !this.data.uniqueCustomerCount) {
        return 0
      }
      return this.data.totalRevenue / this.data.uniqueCustomerCount
    },
    revenueByCategoryType() {
      if (!this.data || !this.data.totalRevenue) {
        return { subscriptions: 0, packages: 0, other: 0 }
      }

      const subscriptions = this.data.revenueBySubscription?.reduce((sum, item) => sum + item.total, 0) || 0
      const packages = this.data.revenueByCategory?.filter((cat) => cat.main === "Pakete").reduce((sum, item) => sum + item.total, 0) || 0
      const other = this.data.totalRevenue - subscriptions - packages

      return { subscriptions, packages, other }
    },
    revenuePercentages() {
      if (!this.data || !this.data.totalRevenue) {
        return { subscriptions: 0, packages: 0, other: 0 }
      }
      const total = this.data.totalRevenue
      // Für die Prozentzahlen nutzen wir die Brutto-Werte, da das Verhältnis gleich bleibt
      const revenues = this.revenueByCategoryType
      return {
        subscriptions: ((revenues.subscriptions / total) * 100).toFixed(1),
        packages: ((revenues.packages / total) * 100).toFixed(1),
        other: ((revenues.other / total) * 100).toFixed(1),
      }
    },
    yoyChartSeries() {
      if (!this.yoyData || !this.yoyData.results[this.yoySelectedStudio]) {
        return []
      }
      // Hier müssen wir aufpassen: Das Array muss transformed werden
      const studioData = this.yoyData.results[this.yoySelectedStudio]
      return Object.keys(studioData).map((year) => ({
        name: year,
        data: studioData[year].map((val) => this.getEffectiveValue(val)), // NEU: Transformiere Werte
      }))
    },
    subscriptionRevenue() {
      if (!this.data?.revenueBySubscription) return { total: 0 }
      return this.data.revenueBySubscription.reduce(
        (acc, curr) => {
          acc.total += curr.total
          return acc
        },
        { total: 0 },
      )
    },
    mainCategorySelectItems() {
      if (!this.data) return []
      const mainCategories = new Set()
      if (this.data.revenueBySubscription?.length > 0) {
        mainCategories.add("Abonnements")
      }
      if (this.data.revenueByCategory) {
        this.data.revenueByCategory.forEach((cat) => mainCategories.add(cat.main))
      }
      return Array.from(mainCategories)
        .sort()
        .map((name) => ({ name: name }))
    },
    chartTitle() {
      if (this.drilldownLevel === 1) return `Details für: ${this.drilldownHistory[1]}`
      return `Umsatz nach ${this.selectedMainCategory}`
    },
    dynamicChartSeries() {
      if (!this.data) return []
      if (this.drilldownLevel === 1) {
        return this.drilldownItemData
          ? this.drilldownItemData
              .map((item) => ({
                x: item.name,
                y: this.getEffectiveValue(item.total), // NEU: Netto
                count: item.count,
              }))
              .sort((a, b) => b.y - a.y)
          : []
      }

      let dataToShow =
        this.selectedMainCategory === "Abonnements"
          ? this.data.revenueBySubscription
          : this.data.revenueByCategory.filter((cat) => cat.main === this.selectedMainCategory)

      return (dataToShow || [])
        .map((item) => ({
          x: item.sub || item.name,
          y: this.getEffectiveValue(item.total), // NEU: Netto
          count: item.count,
        }))
        .sort((a, b) => b.y - a.y)
    },
    dynamicChartOptions() {
      return {
        chart: { type: "bar", foreColor: "#9CA3AF", events: { dataPointSelection: this.handleChartClick }, toolbar: { show: false } },
        xaxis: { categories: this.dynamicChartSeries.map((item) => item.x) },
        yaxis: { labels: { formatter: (val) => this.formatCurrency(val) } },
        tooltip: {
          theme: "dark",
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const count = w.config.series[seriesIndex].data[dataPointIndex].count
            const revenue = series[seriesIndex][dataPointIndex]
            const categoryName = w.globals.labels[dataPointIndex]

            return `<div class="pa-2">
                            <div><strong>${categoryName}</strong></div>
                            <div>Umsatz: <strong>${this.formatCurrency(revenue)}</strong></div>
                            <div>Verkäufe: <strong>${count} Stk.</strong></div>
                        </div>`
          },
        },
        plotOptions: { bar: { borderRadius: 4, horizontal: false, columnWidth: "60%" } },
        dataLabels: { enabled: false },
        grid: { borderColor: "#374151", strokeDashArray: 4 },
        colors: [this.chartColors[0]],
      }
    },
    revenueOverTimeSeries() {
      if (!this.data?.revenueOverTime) return []
      return [
        {
          name: "Umsatz",
          data: this.data.revenueOverTime.map((d) => [
            new Date(d.date).getTime(),
            this.getEffectiveValue(d.total), // NEU: Netto
          ]),
        },
      ]
    },
    revenueOverTimeOptions() {
      return {
        chart: { type: "area", foreColor: "#9CA3AF", height: 350, zoom: { enabled: false }, toolbar: { show: false } },
        title: { text: "Umsatzentwicklung im Zeitraum", align: "left", style: { fontSize: "18px", color: "#F9FAFB" } },
        colors: [this.chartColors[1]],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1, stops: [0, 90, 100] } },
        xaxis: { type: "datetime", labels: { datetimeUTC: false, format: "dd. MMM" } },
        yaxis: { labels: { formatter: (val) => this.formatCurrency(val) } },
        tooltip: { theme: "dark", x: { format: "dd. MMMM yyyy" }, y: { formatter: (val) => this.formatCurrency(val) } },
        grid: { borderColor: "#374151", strokeDashArray: 4 },
      }
    },
    v2RevenueOverTimeOptions() {
      return {
        ...this.revenueOverTimeOptions,
        title: { text: undefined },
        colors: [this.$vuetify.theme.currentTheme.primary || this.chartColors[0]],
      }
    },
    mainCategorySeries() {
      if (!this.data) return { series: [], labels: [] }
      const categories = {}
      this.data.revenueByCategory.forEach((item) => {
        if (!categories[item.main]) categories[item.main] = 0
        categories[item.main] += this.getEffectiveValue(item.total) // NEU: Netto
      })
      if (this.subscriptionRevenue.total > 0) {
        categories["Abonnements"] = this.getEffectiveValue(this.subscriptionRevenue.total) // NEU: Netto
      }
      return { series: Object.values(categories), labels: Object.keys(categories) }
    },
    mainCategoryOptions() {
      return {
        chart: { type: "donut", foreColor: "#ccc" },
        title: { text: "Umsatz nach Hauptkategorie", align: "left", style: { fontSize: "18px", color: "#F9FAFB" } },
        labels: this.mainCategorySeries.labels,
        colors: this.chartColors,
        tooltip: { theme: "dark", y: { formatter: (val) => this.formatCurrency(val) } },
        legend: { position: "bottom" },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                total: { show: true, label: "Gesamt", formatter: (w) => this.formatCurrency(w.globals.seriesTotals.reduce((a, b) => a + b, 0)) },
              },
            },
          },
        },
      }
    },
    genderSeries() {
      if (!this.data?.revenueByGender) return { series: [], labels: [] }
      const { male, female, other } = this.data.revenueByGender
      // NEU: Netto
      return {
        series: [this.getEffectiveValue(male.total), this.getEffectiveValue(female.total), this.getEffectiveValue(other.total)],
        labels: ["Männlich", "Weiblich", "Andere"],
      }
    },
    genderOptions() {
      return {
        chart: { type: "pie", foreColor: "#ccc" },
        title: { text: "Umsatz nach Geschlecht", align: "left", style: { fontSize: "18px", color: "#F9FAFB" } },
        colors: [this.chartColors[4], this.chartColors[5], this.chartColors[6]],
        labels: this.genderSeries.labels,
        tooltip: { theme: "dark", y: { formatter: (val) => this.formatCurrency(val), title: { formatter: (s) => s } } },
        legend: { position: "bottom" },
      }
    },
    filteredDetailedTransactions() {
      let items = this.detailedTransactions
      if (this.filters.customer) {
        const s = this.filters.customer.toLowerCase()
        items = items.filter((i) => `${i.FIRSTNAME || ""} ${i.LASTNAME || ""}`.toLowerCase().includes(s))
      }
      if (this.filters.contractName) {
        const s = this.filters.contractName.toLowerCase()
        items = items.filter((i) => i.ContractName && i.ContractName.toLowerCase().includes(s))
      }
      if (this.filters.description) {
        const s = this.filters.description.toLowerCase()
        items = items.filter((i) => i.DESCRIPTION && i.DESCRIPTION.toLowerCase().includes(s))
      }
      if (this.filters.accountName) {
        const s = this.filters.accountName.toLowerCase()
        items = items.filter((i) => i.AccountName && i.AccountName.toLowerCase().includes(s))
      }
      if (this.filters.hasAdjustment) {
        items = items.filter((item) => item.adjustment)
      }

      return items
    },
    filteredTotalValue() {
      return this.filteredDetailedTransactions.reduce((sum, item) => {
        const valueToAdd = item.netValue !== null ? item.netValue : item.VALUE
        return sum + Math.abs(this.getEffectiveValue(valueToAdd)) // NEU: Netto
      }, 0)
    },
    uniqueContractNames() {
      const names = this.detailedTransactions.map((t) => t.ContractName).filter(Boolean)
      return [...new Set(names)].sort()
    },
    uniqueAccountNames() {
      const names = this.detailedTransactions.map((t) => t.AccountName).filter(Boolean)
      return [...new Set(names)].sort()
    },
    quarterlyChartOptions() {
      return {
        ...this.yoyChartOptions,
        xaxis: { categories: ["Q1", "Q2", "Q3", "Q4"] },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        dataLabels: { enabled: false },
        tooltip: {
          theme: "dark",
          shared: true,
          intersect: false,
          y: {
            formatter: (val, { seriesIndex, dataPointIndex, w }) => {
              const currentYearName = w.globals.seriesNames[seriesIndex]

              const chronologicalYears = [...this.yoyComparisonData.years].reverse()
              const currentYearData = chronologicalYears.find((y) => y.year == currentYearName)
              const previousYearData = chronologicalYears.find((y) => y.year == currentYearName - 1)

              if (!currentYearData) return this.formatCurrency(val)

              const previousQuarterValue = previousYearData ? previousYearData.quarterlyData[dataPointIndex] : 0

              if (previousQuarterValue > 0) {
                const growth = ((val - previousQuarterValue) / previousQuarterValue) * 100
                const sign = growth > 0 ? "+" : ""
                const color = growth > 0 ? "#10B981" : "#EF4444"
                return `${this.formatCurrency(val)} <span style="font-weight: 600; color: ${color};">(${sign}${growth.toFixed(1)}%)</span>`
              }

              return this.formatCurrency(val)
            },
          },
        },
      }
    },
    yoyComparisonData() {
      if (!this.aggregatedStudioDataForYoY) {
        return { years: [], kpis: {} }
      }

      const studioData = this.aggregatedStudioDataForYoY
      const years = [...this.yoyData.years].sort((a, b) => a - b)
      const today = new Date()
      const currentYear = today.getFullYear()
      const currentMonthIndex = today.getMonth()
      const currentDay = today.getDate()
      let allTimeBestMonth = { year: 0, month: "", revenue: 0 }
      let bestGrowthMonth = { year: 0, month: "", growth: -Infinity }

      const processedYears = []

      years.forEach((year) => {
        // NEU: WICHTIG! Wir wandeln die Rohdaten HIER um, damit alle Berechnungen (Kumuliert, Q1-Q4) Netto sind
        const yearData = studioData[year].map((val) => this.getEffectiveValue(val))

        // Auch die Vorjahresdaten müssen transformiert werden für den Vergleich
        const rawPreviousYearData = studioData[year - 1]
        const previousYearData = rawPreviousYearData ? rawPreviousYearData.map((val) => this.getEffectiveValue(val)) : null

        const monthlyDetails = yearData.map((revenue, i) => {
          if (revenue > allTimeBestMonth.revenue) {
            allTimeBestMonth = { year, month: i, revenue }
          }

          const prevMonthRevenue = previousYearData ? previousYearData[i] : 0

          let growth = 0
          if (prevMonthRevenue && prevMonthRevenue > 0) {
            growth = ((revenue - prevMonthRevenue) / prevMonthRevenue) * 100
            if (growth > bestGrowthMonth.growth) {
              bestGrowthMonth = { year, month: i, growth }
            }
          }
          return { revenue, growth }
        })

        processedYears.push({
          year,
          total: yearData.reduce((sum, r) => sum + r, 0),
          monthlyDetails,
          cumulativeData: yearData.reduce((acc, val) => [...acc, (acc.length > 0 ? acc[acc.length - 1] : 0) + val], []),
          quarterlyData: [0, 1, 2, 3].map((q) => yearData.slice(q * 3, q * 3 + 3).reduce((sum, r) => sum + r, 0)),
        })
      })

      const thisYear = processedYears.find((p) => p.year === currentYear)
      const lastYear = processedYears.find((p) => p.year === currentYear - 1)
      let ytdGrowth = 0,
        ytdCurrent = 0,
        ytdPrevious = 0
      if (thisYear && lastYear) {
        const daysInCurrentMonth = new Date(currentYear, currentMonthIndex + 1, 0).getDate()
        ytdCurrent =
          thisYear.monthlyDetails.slice(0, currentMonthIndex).reduce((sum, m) => sum + m.revenue, 0) +
          (thisYear.monthlyDetails[currentMonthIndex].revenue / daysInCurrentMonth) * (currentDay - 1)
        const daysInPreviousMonth = new Date(currentYear - 1, currentMonthIndex + 1, 0).getDate()
        ytdPrevious =
          lastYear.monthlyDetails.slice(0, currentMonthIndex).reduce((sum, m) => sum + m.revenue, 0) +
          (lastYear.monthlyDetails[currentMonthIndex].revenue / daysInPreviousMonth) * (currentDay - 1)
        if (ytdPrevious > 0) {
          ytdGrowth = ((ytdCurrent - ytdPrevious) / ytdPrevious) * 100
        }
      }
      const months = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"]

      return {
        years: processedYears.reverse(),
        kpis: {
          ytdCurrent,
          ytdPrevious,
          ytdGrowth,
          bestMonth: `${months[allTimeBestMonth.month]} ${allTimeBestMonth.year}`,
          bestMonthRevenue: allTimeBestMonth.revenue,
          bestGrowth: `${months[bestGrowthMonth.month]} ${bestGrowthMonth.year}`,
          bestGrowthValue: bestGrowthMonth.growth,
        },
      }
    },
    mainYoYChartSeries() {
      if (!this.yoyComparisonData.years || this.yoyComparisonData.years.length === 0) return []
      const latestYear = this.yoyComparisonData.years[0]
      const otherYears = this.yoyComparisonData.years.slice(1)

      let series = [
        {
          name: latestYear.year.toString(),
          type: "bar",
          data: latestYear.monthlyDetails.map((m) => m.revenue),
        },
      ]

      otherYears.forEach((year) => {
        series.push({
          name: year.year.toString(),
          type: "bar",
          data: year.monthlyDetails.map((m) => m.revenue),
        })
      })
      return series.reverse()
    },
    cumulativeChartOptions() {
      return {
        ...this.yoyChartOptions,
        chart: { type: "line", foreColor: "#9CA3AF", toolbar: { show: true } },
        stroke: { width: 3, curve: "smooth" },
        markers: { size: 0 },
        tooltip: {
          theme: "dark",
          shared: true,
          intersect: false,
          y: {
            formatter: (val, { seriesIndex, dataPointIndex, w }) => {
              const currentYearName = w.globals.seriesNames[seriesIndex]

              const chronologicalYears = [...this.yoyComparisonData.years].reverse()
              const currentYearData = chronologicalYears.find((y) => y.year == currentYearName)
              const previousYearData = chronologicalYears.find((y) => y.year == currentYearName - 1)

              if (!currentYearData) return this.formatCurrency(val)

              const previousCumulativeValue = previousYearData ? previousYearData.cumulativeData[dataPointIndex] : 0

              if (previousCumulativeValue > 0) {
                const growth = ((val - previousCumulativeValue) / previousCumulativeValue) * 100
                const sign = growth > 0 ? "+" : ""
                const color = growth > 0 ? "#10B981" : "#EF4444"
                return `${this.formatCurrency(val)} <span style="font-weight: 600; color: ${color};">(${sign}${growth.toFixed(1)}%)</span>`
              }

              return this.formatCurrency(val)
            },
          },
        },
      }
    },
    cumulativeChartSeries() {
      if (!this.yoyComparisonData.years) return []
      return this.yoyComparisonData.years
        .map((year) => ({
          name: year.year.toString(),
          data: year.cumulativeData,
        }))
        .reverse()
    },
    quarterlyChartSeries() {
      if (!this.yoyComparisonData.years) return []
      return this.yoyComparisonData.years
        .map((year) => ({
          name: year.year.toString(),
          data: year.quarterlyData,
        }))
        .reverse()
    },
    yoySplitChartData() {
      if (!this.yoyComparisonData.years || this.yoyComparisonData.years.length < 2) return []

      const charts = []
      const chronologicalYears = [...this.yoyComparisonData.years].reverse()

      for (let i = 1; i < chronologicalYears.length; i++) {
        const currentYearStats = chronologicalYears[i]
        const previousYearStats = chronologicalYears[i - 1]
        charts.unshift({
          title: `${previousYearStats.year} vs. ${currentYearStats.year}`,
          series: [
            { name: previousYearStats.year.toString(), data: previousYearStats.monthlyDetails.map((m) => m.revenue) },
            { name: currentYearStats.year.toString(), data: currentYearStats.monthlyDetails.map((m) => m.revenue) },
          ],
          options: {
            ...this.yoyChartOptions,
            chart: { type: "bar", foreColor: "#9CA3AF", toolbar: { show: true } },
            stroke: { show: true, width: 2, colors: ["transparent"] },
            markers: { size: 0 },
            dataLabels: { enabled: false },
          },
        })
      }
      return charts
    },
    yoyChartOptions() {
      return {
        chart: { type: "bar", foreColor: "#9CA3AF", toolbar: { show: true } },
        stroke: { width: [0, 3, 3, 3, 3, 3, 3], curve: "smooth" },
        markers: { size: 4 },
        xaxis: {
          categories: ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"],
        },
        yaxis: { title: { text: "Umsatz (CHF)" }, labels: { formatter: (val) => this.formatCurrency(val) } },
        tooltip: {
          theme: "dark",
          shared: true,
          intersect: false,
          y: {
            formatter: (val, { seriesIndex, dataPointIndex, w }) => {
              const year = w.globals.seriesNames[seriesIndex]
              const yearStats = this.yoyComparisonData.years.find((d) => d.year == year)
              if (!yearStats) return this.formatCurrency(val)
              const monthStats = yearStats.monthlyDetails[dataPointIndex]
              const growth = monthStats.growth
              const oldestYear = [...this.yoyData.years].sort((a, b) => a - b)[0]
              if (growth !== 0 && yearStats.year > oldestYear) {
                const sign = growth > 0 ? "+" : ""
                const color = growth > 0 ? "#10B981" : "#EF4444"
                return `${this.formatCurrency(val)} <span style="font-weight: 600; color: ${color};">(${sign}${growth.toFixed(1)}%)</span>`
              }
              return this.formatCurrency(val)
            },
          },
        },
        grid: { borderColor: "#374151", strokeDashArray: 4 },
        colors: this.chartColors,
        legend: { position: "top", horizontalAlign: "left" },
      }
    },
  },
  watch: {
    yoySelectedStudios(newSelection, oldSelection) {
      if (!oldSelection) return

      const justAddedGesamt = newSelection.includes("Gesamt") && !oldSelection.includes("Gesamt")
      const justRemovedGesamt = !newSelection.includes("Gesamt") && oldSelection.includes("Gesamt")
      const justAddedStudio = newSelection.length > oldSelection.length && !justAddedGesamt

      if (justAddedGesamt) {
        this.yoySelectedStudios = ["Gesamt"]
        return
      }

      if (justAddedStudio && newSelection.length > 1 && oldSelection.includes("Gesamt")) {
        this.yoySelectedStudios = newSelection.filter((s) => s !== "Gesamt")
        return
      }

      if (newSelection.length === 0) {
        this.yoySelectedStudios = ["Gesamt"]
      }
    },
    yoyData(newData) {
      if (newData && newData.years && newData.years.length >= 2) {
        this.compareYearA = newData.years[0]
        this.compareYearB = newData.years[1]
      }
    },
    compareYearA(newYearA) {
      if (newYearA === this.compareYearB) {
        this.compareYearB = this.availableYearsForComparison.find((y) => y !== newYearA) || null
      }
    },
    yoyYearsBack() {
      if (this.showYoyDialog) this.fetchYoyData()
    },
    async selectedStatisticsVersion() {
      this.drillUp()
      if (this.selectedStatisticsVersion === "v2") {
        await Promise.all([this.fetchTransactionStatistics(), this.refreshForecastV2()])
      } else {
        await this.fetchTransactionStatistics()
      }
    },
    async selectedDataType() {
      this.drillUp()
      await Promise.all([
        this.fetchTransactionStatistics(),
        this.selectedStatisticsVersion === "v2" ? this.fetchForecastComparisonV2() : Promise.resolve(),
      ])
      if (this.showForecastComparisonDialog) await this.fetchForecastCancellations()
      if (this.showYoyDialog) {
        this.fetchYoyData()
      }
    },
    async datePickerRange() {
      await Promise.all([
        this.fetchTransactionStatistics(),
        this.fetchInvoicesStatistics(),
        this.selectedStatisticsVersion === "v2" ? this.refreshForecastV2() : Promise.resolve(),
      ])
      if (this.showForecastComparisonDialog) await this.fetchForecastCancellations()
    },
    async studios() {
      await Promise.all([
        this.fetchTransactionStatistics(),
        this.fetchInvoicesStatistics(),
        this.selectedStatisticsVersion === "v2" ? this.refreshForecastV2() : Promise.resolve(),
      ])
      if (this.showForecastComparisonDialog) await this.fetchForecastCancellations()
    },
    showDetailsDialog(isOpening) {
      if (isOpening) {
        this.fetchTransactionDetails()
      } else {
        this.filters = { customer: "", contractName: "", description: "", accountName: "", hasAdjustment: false }
      }
    },
    selectedMainCategory() {
      this.drillUp()
    },
  },
}
</script>

<style lang="scss" scoped>
.transaction-toolbar-row {
  margin-bottom: 2px;
}

.transaction-toolbar {
  gap: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.stats-version-control {
  display: flex;
  align-items: center;
  gap: 9px;
}

.stats-version-control > span {
  color: #778396;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.stats-version-control ::v-deep .v-btn--active {
  color: #76d2ff;
}

.stats-version-control ::v-deep .v-btn--active .v-icon {
  color: #76d2ff !important;
}

.kpi-card {
  background-color: #1f2937;
  border: 1px solid #374151;
  transition: all 0.2s ease-in-out;
}
.kpi-card[hover]:hover {
  transform: translateY(-4px);
  border-color: #4f5a6e;
  cursor: pointer;
}

.forecast-shell {
  background: transparent;
  border: 0;
  border-top: 2px solid #42b8f5;
  border-radius: 0;
  box-shadow: none !important;
}

.forecast-header {
  background: transparent;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}

.forecast-icon {
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: rgba(66, 184, 245, 0.09);
  border: 1px solid rgba(66, 184, 245, 0.34);
  box-shadow: inset 0 0 18px rgba(66, 184, 245, 0.06);
}

.forecast-accent-text {
  color: #76d2ff !important;
}

.forecast-title {
  font-size: clamp(1.25rem, 2.2vw, 1.8rem);
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.forecast-subtitle {
  max-width: 820px;
  color: #a7b5c8;
  line-height: 1.5;
}

.forecast-loading {
  background: transparent;
}

.forecast-history-context {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 22px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.forecast-history-context h3 {
  margin: 0;
  color: #f3f5f8;
  font-size: 1.05rem;
}

.forecast-history-context p {
  max-width: 920px;
  margin: 6px 0 0;
  color: #8b96a7;
  font-size: 0.8rem;
  line-height: 1.5;
}

.forecast-range-note,
.forecast-empty-schedule {
  display: flex;
  align-items: flex-start;
  padding: 12px 0;
  color: #929dad;
  font-size: 0.76rem;
  line-height: 1.45;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.forecast-empty-schedule strong,
.forecast-empty-schedule span {
  display: block;
}

.forecast-empty-schedule strong {
  margin-bottom: 3px;
  color: #e6eaf0;
}

.forecast-kpi {
  width: 100%;
  min-height: 126px;
  padding: 17px;
  text-align: left;
  color: inherit;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 12px;
  background: transparent;
  transition: transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
}

.forecast-kpi:hover,
.forecast-kpi:focus-visible {
  transform: translateY(-2px);
  border-color: rgba(66, 184, 245, 0.62);
  box-shadow: 0 10px 25px rgba(2, 10, 20, 0.24);
  outline: none;
}

.forecast-kpi--primary {
  background: linear-gradient(145deg, rgba(66, 184, 245, 0.09), rgba(66, 184, 245, 0.015) 58%);
  border-color: rgba(66, 184, 245, 0.46);
}

.forecast-kpi__label {
  color: #9eacc0;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.055em;
  text-transform: uppercase;
}

.forecast-kpi__value {
  margin-top: 8px;
  color: #f8fafc;
  font-size: clamp(1.35rem, 2vw, 1.8rem);
  font-weight: 800;
  letter-spacing: -0.035em;
}

.forecast-kpi__meta {
  margin-top: 8px;
  color: #8291a7;
  font-size: 0.75rem;
  line-height: 1.35;
}

.forecast-audit-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.13);
  border-radius: 11px;
  background: transparent;
}

.forecast-audit-strip > div,
.forecast-audit-strip > button {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 14px;
  color: inherit;
  text-align: left;
  border: 0;
  border-right: 1px solid rgba(148, 163, 184, 0.11);
  background: transparent;
}

.forecast-audit-strip > :last-child {
  border-right: 0;
}

.forecast-audit-strip > button:hover,
.forecast-audit-strip > button:focus-visible {
  background: rgba(255, 255, 255, 0.05);
  outline: none;
}

.forecast-audit-strip span {
  color: #78899f;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.forecast-audit-strip strong {
  color: #eaf1fa;
  font-size: 0.82rem;
}

.forecast-panel {
  overflow: hidden;
  color: #dce5f2;
  background: transparent !important;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 12px !important;
}

.forecast-quality {
  color: #69f0ae;
  font-size: 1.45rem;
  font-weight: 800;
}

.forecast-month {
  width: 100%;
  display: grid;
  grid-template-columns: 80px minmax(80px, 1fr) 55px 118px;
  align-items: center;
  gap: 10px;
  min-height: 39px;
  padding: 5px 4px;
  color: #dce5f2;
  text-align: left;
  border-radius: 8px;
  transition: background 150ms ease;
}

.forecast-month:hover,
.forecast-month:focus-visible {
  background: rgba(255, 255, 255, 0.05);
  outline: none;
}

.forecast-month__name {
  color: #b9c5d5;
  font-size: 0.78rem;
  text-transform: capitalize;
}

.forecast-month__bar {
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: #26364a;
}

.forecast-month__bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #168fcf, #76d2ff);
}

.forecast-month__count {
  color: #8494aa;
  font-size: 0.76rem;
  text-align: right;
}

.forecast-month__amount {
  color: #f4f7fb;
  font-size: 0.78rem;
  font-weight: 700;
  text-align: right;
}

.forecast-source-list {
  max-height: 270px;
  overflow: auto;
}

.forecast-source-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0;
  border-top: 1px solid rgba(148, 163, 184, 0.11);
}

.forecast-table ::v-deep tbody tr {
  cursor: pointer;
}

.forecast-table ::v-deep tbody tr:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

.forecast-comparison {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0;
  background: transparent;
}

.comparison-values {
  gap: 16px;
}

.comparison-values > div {
  display: flex;
  flex-direction: column;
  min-width: 108px;
}

.comparison-values span {
  color: #8190a5;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.comparison-values strong {
  color: #edf4fd;
  font-size: 0.88rem;
}

.forecast-detail-body {
  padding-top: 84px !important;
}

.forecast-detail-table ::v-deep .v-data-table__expanded__content {
  box-shadow: inset 0 7px 18px rgba(0, 0, 0, 0.22) !important;
}

.forecast-evidence {
  background: #121720;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.evidence-title {
  margin-bottom: 10px;
  color: #76d2ff;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.evidence-list {
  margin: 0;
}

.evidence-list > div {
  display: grid;
  grid-template-columns: minmax(95px, 1fr) minmax(80px, 1fr);
  gap: 8px;
  padding: 4px 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.08);
}

.evidence-list dt {
  color: #798aa1;
  font-size: 0.7rem;
}

.evidence-list dd {
  margin: 0;
  color: #e4ecf7;
  font-size: 0.75rem;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.business-forecast {
  padding: 6px 0 0;
  background: transparent;
  border: 0;
  border-radius: 0;
}

.business-forecast__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}

.business-eyebrow {
  margin-bottom: 4px;
  color: #76d2ff;
  font-size: 0.69rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.business-title {
  margin: 0;
  color: #f5f7fa;
  font-size: clamp(1.05rem, 1.8vw, 1.35rem);
  line-height: 1.25;
}

.business-subtitle {
  max-width: 760px;
  margin-top: 5px;
  color: #929dad;
  font-size: 0.82rem;
  line-height: 1.45;
}

.business-control {
  flex: 0 0 auto;
}

.business-control > span {
  display: block;
  margin-bottom: 5px;
  color: #7f8998;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.business-control ::v-deep .v-btn-toggle {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.business-source-rail {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
  padding: 0;
  background: transparent;
  border: 0;
}

.business-notices {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.business-notices > div {
  display: flex;
  align-items: flex-start;
  padding: 9px 2px;
  color: #929dad;
  font-size: 0.72rem;
  line-height: 1.45;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.business-source-pill {
  display: flex;
  max-width: 100%;
  align-items: center;
  gap: 7px;
  padding: 6px 9px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
}

.business-source-rail strong {
  min-width: 0;
  color: inherit;
  font-size: 0.7rem;
}

.business-source-rail em {
  color: inherit;
  font-size: 0.65rem;
  font-style: normal;
  opacity: 0.82;
}

.business-source-pill--confirmed { color: #70d39d; border-color: rgba(86, 200, 139, 0.35); }
.business-source-pill--contract { color: #76d2ff; border-color: rgba(66, 184, 245, 0.5); }
.business-source-pill--plan { color: #f2b84b; border-color: rgba(242, 184, 75, 0.38); }
.business-source-pill--negative { color: #ff8d8d; border-color: rgba(255, 123, 123, 0.4); }

.business-source-dot {
  width: 8px;
  height: 8px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: #8b95a5;
}

.business-source-dot--confirmed { background: #56c88b; }
.business-source-dot--contract { background: #42b8f5; }
.business-source-dot--plan { background: #f2b84b; }

.business-scenario {
  --scenario-accent: #9bafc4;
  --scenario-border: rgba(155, 175, 196, 0.34);
  --scenario-tint: rgba(126, 151, 176, 0.09);
  position: relative;
  width: 100%;
  min-height: 160px;
  padding: 15px;
  overflow: hidden;
  color: inherit;
  text-align: left;
  background: linear-gradient(145deg, var(--scenario-tint), rgba(255, 255, 255, 0.008) 62%);
  border: 1px solid var(--scenario-border);
  border-top: 2px solid var(--scenario-accent);
  border-radius: 11px;
  transition: border-color 150ms ease, transform 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.business-scenario:hover,
.business-scenario:focus-visible {
  transform: translateY(-2px);
  border-color: var(--scenario-accent);
  box-shadow: 0 12px 28px rgba(2, 10, 20, 0.22);
  outline: none;
}

.business-scenario--cautious {
  --scenario-accent: #9bafc4;
  --scenario-border: rgba(155, 175, 196, 0.34);
  --scenario-tint: rgba(126, 151, 176, 0.09);
}

.business-scenario--plan {
  --scenario-accent: #f2b84b;
  --scenario-border: rgba(242, 184, 75, 0.4);
  --scenario-tint: rgba(242, 184, 75, 0.085);
}

.business-scenario--strong {
  --scenario-accent: #56c88b;
  --scenario-border: rgba(86, 200, 139, 0.4);
  --scenario-tint: rgba(86, 200, 139, 0.085);
}

.business-scenario__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--scenario-accent);
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.business-scenario__icon {
  width: 30px;
  height: 30px;
  color: var(--scenario-accent) !important;
  caret-color: var(--scenario-accent) !important;
  border: 1px solid var(--scenario-border);
  border-radius: 9px;
  background: var(--scenario-tint);
}

.business-scenario__amount {
  margin-top: 9px;
  color: #f7f9fc;
  font-size: clamp(1.35rem, 2.2vw, 1.8rem);
  font-weight: 800;
  letter-spacing: -0.035em;
}

.business-scenario__unit {
  color: #7f8998;
  font-size: 0.67rem;
}

.business-scenario__bar {
  display: flex;
  height: 5px;
  margin-top: 10px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 999px;
}

.business-scenario__bar-confirmed { background: #56c88b; }
.business-scenario__bar-forecast { background: #42b8f5; }

.business-scenario__bridge {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  font-size: 0.68rem;
  font-weight: 600;
}

.business-scenario__bridge-confirmed { color: #70d39d; }
.business-scenario__bridge-forecast { color: #76d2ff; }

.business-facts {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  overflow: hidden;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
}

.business-facts > button,
.business-facts > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 12px;
  color: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  border-right: 1px solid rgba(255, 255, 255, 0.07);
}

.business-facts > :last-child { border-right: 0; }
.business-facts > button:hover { background: rgba(255, 255, 255, 0.04); }
.business-facts span { color: #788393; font-size: 0.64rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; }
.business-facts strong { margin: 3px 0; color: #f1f4f8; font-size: 1.1rem; }
.business-facts small { color: #727d8d; font-size: 0.65rem; line-height: 1.35; }
.business-facts > :nth-child(1) strong { color: #76d2ff; }
.business-facts > :nth-child(2) strong { color: #70d39d; }
.business-facts > :nth-child(3) strong { color: #b59cf6; }
.business-facts > :nth-child(4) strong { color: #f2b84b; }
.business-facts > :nth-child(5) strong { color: #70d39d; }

.business-panel {
  overflow-x: auto;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 11px;
}

.business-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.business-month {
  width: 100%;
  min-width: 760px;
  display: grid;
  grid-template-columns: 88px 1.25fr 0.9fr 0.9fr 1.15fr 1.05fr;
  align-items: center;
  gap: 12px;
  min-height: 54px;
  padding: 7px 14px;
  color: #dce2eb;
  text-align: left;
  background: transparent;
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.business-month:not(.business-month--head):hover { background: rgba(255, 255, 255, 0.035); }
.business-month--head { min-height: 34px; color: #6f7b8c; font-size: 0.62rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; }
.business-month > span { display: flex; min-width: 0; flex-direction: column; }
.business-month strong { color: inherit; font-size: 0.76rem; }
.business-month small { margin-top: 2px; color: #707c8d; font-size: 0.62rem; }
.business-month__name { color: #b9c2cf; font-size: 0.75rem; font-weight: 700; text-transform: capitalize; }

.business-month em {
  align-self: flex-start;
  padding: 2px 6px;
  font-size: 0.61rem;
  font-style: normal;
  font-weight: 700;
  border: 1px solid currentColor;
  border-radius: 999px;
}

.business-coverage--covered { color: #56c88b; }
.business-coverage--partial { color: #f2b84b; }
.business-coverage--gap { color: #ff7b7b; }
.business-coverage--neutral { color: #7d8796; }
.business-coverage--outside { color: #a58bd4; }

.business-gap-note {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 12px;
  padding: 4px 0;
  color: #a9b1bd;
  font-size: 0.7rem;
  line-height: 1.45;
  background: transparent;
  border: 0;
}

.business-gap-note > span {
  width: 7px;
  height: 7px;
  flex: 0 0 auto;
  background: #ff7b7b;
  border-radius: 50%;
}

.business-product-gaps button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  color: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.business-product-gaps button:hover { background: rgba(255, 255, 255, 0.04); }
.business-product-gaps button > span { display: flex; min-width: 0; flex-direction: column; }
.business-product-gaps strong { color: #e8ecf2; font-size: 0.73rem; }
.business-product-gaps small { color: #707c8d; font-size: 0.62rem; }
.business-gap-count { flex: 0 0 auto; text-align: right; }
.business-gap-count strong { color: #ff9696; font-size: 0.95rem; }

.v2-actual-section {
  padding: 22px 0 4px;
  border-top: 2px solid #56c88b;
}

.v2-actual-section .business-eyebrow {
  color: #70d39d;
}

.v2-actual-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}

.v2-actual-head h2 {
  margin: 0;
  color: #f5f7fa;
  font-size: clamp(1.25rem, 2vw, 1.7rem);
  letter-spacing: -0.025em;
}

.v2-actual-head p {
  max-width: 780px;
  margin: 6px 0 0;
  color: #8b96a7;
  font-size: 0.8rem;
  line-height: 1.5;
}

.v2-actual-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
}

.v2-forecast-switch {
  margin: 0;
  padding: 0;
}

.v2-forecast-switch ::v-deep .v-label {
  color: #dce3ec;
  font-size: 0.72rem;
  font-weight: 700;
}

.v2-source-badge {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 7px;
  padding: 7px 10px;
  color: #a7b0bd;
  font-size: 0.7rem;
  font-weight: 700;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
}

.v2-status-note {
  display: flex;
  align-items: flex-start;
  padding: 10px 0;
  color: #9ba5b3;
  font-size: 0.75rem;
  line-height: 1.45;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.v2-status-note--error {
  color: #ffaaa8;
}

.v2-kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 11px;
}

.v2-kpi {
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 14px;
  color: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.v2-kpi:last-child {
  border-right: 0;
}

button.v2-kpi:hover:not(:disabled),
button.v2-kpi:focus-visible:not(:disabled) {
  background: rgba(255, 255, 255, 0.035);
  outline: none;
}

button.v2-kpi:disabled {
  cursor: default;
}

.v2-kpi--primary {
  box-shadow: inset 0 2px 0 #56c88b;
}

.v2-kpi--mixed {
  box-shadow: inset 0 2px 0 #56c88b, inset 0 -2px 0 #42b8f5;
}

.v2-kpi > span,
.v2-actual-meta span {
  color: #788393;
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.055em;
  text-transform: uppercase;
}

.v2-kpi > strong {
  margin: 5px 0 3px;
  color: #f1f4f8;
  font-size: clamp(1rem, 1.5vw, 1.25rem);
  overflow-wrap: anywhere;
}

.v2-kpi > small {
  color: #747f8f;
  font-size: 0.65rem;
  line-height: 1.4;
}

.v2-revenue-mix {
  margin-top: 5px;
}

.v2-revenue-mix__bar {
  display: flex;
  height: 5px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 999px;
}

.v2-revenue-mix__actual { background: #56c88b; }
.v2-revenue-mix__forecast { background: #42b8f5; }

.v2-revenue-mix__labels {
  display: flex;
  flex-wrap: wrap;
  gap: 5px 10px;
  margin-top: 6px;
  color: #8792a2;
  font-size: 0.61rem;
  font-weight: 650;
}

.v2-color-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: 4px;
  border-radius: 50%;
}

.v2-color-dot--actual { background: #56c88b; }
.v2-color-dot--forecast { background: #42b8f5; }

.v2-actual-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.v2-actual-meta > div {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px 12px;
  border-right: 1px solid rgba(255, 255, 255, 0.07);
}

.v2-actual-meta > div:last-child {
  border-right: 0;
}

.v2-actual-meta strong {
  color: #e8ecf2;
  font-size: 0.76rem;
}

.v2-actual-panel {
  overflow: hidden;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 11px;
}

.v2-panel-title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.v2-panel-title strong,
.v2-panel-title span {
  display: block;
}

.v2-panel-title strong {
  color: #eef1f5;
  font-size: 0.85rem;
}

.v2-panel-title span {
  margin-top: 2px;
  color: #768293;
  font-size: 0.69rem;
}

.v2-studio-table ::v-deep tbody tr:hover {
  background: rgba(255, 255, 255, 0.035) !important;
}

.v2-empty {
  color: #778292;
  font-size: 0.78rem;
  text-align: center;
}

.forecast-section-label {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding-left: 2px;
}

.forecast-section-label span {
  color: #eff2f6;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.forecast-section-label small { color: #737f8f; font-size: 0.68rem; }

@media (max-width: 1280px) {
  .business-facts {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .v2-kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .v2-kpi {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .v2-kpi:nth-child(3n) {
    border-right: 0;
  }
}

@media (max-width: 700px) {
  .transaction-toolbar,
  .transaction-toolbar > div:first-child,
  .stats-version-control,
  .expense-actions {
    width: 100%;
  }

  .stats-version-control {
    align-items: flex-start;
    flex-direction: column;
    margin-right: 0 !important;
    margin-bottom: 10px;
  }

  .stats-version-control ::v-deep .v-btn-toggle,
  .expense-actions .v-btn {
    flex: 1 1 0;
  }

  .stats-version-control ::v-deep .v-btn-toggle {
    width: 100%;
  }

  .stats-version-control ::v-deep .v-btn {
    flex: 1 1 0;
  }

  .v2-actual-head {
    flex-direction: column;
    gap: 12px;
  }

  .v2-actual-actions {
    width: 100%;
    justify-content: space-between;
  }

  .v2-kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .v2-kpi {
    border-right: 1px solid rgba(255, 255, 255, 0.08);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .v2-kpi:nth-child(3n) {
    border-right: 1px solid rgba(255, 255, 255, 0.08);
  }

  .v2-kpi:nth-child(2n) {
    border-right: 0;
  }

  .v2-actual-meta {
    grid-template-columns: 1fr;
  }

  .v2-actual-meta > div {
    border-right: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  }

  .business-forecast {
    padding: 6px 0 0;
  }

  .business-forecast__head {
    flex-direction: column;
    gap: 14px;
  }

  .business-control {
    width: 100%;
  }

  .business-control ::v-deep .v-btn-toggle {
    width: 100%;
  }

  .business-control ::v-deep .v-btn {
    flex: 1 1 0;
  }

  .business-source-rail {
    align-items: center;
  }

  .business-facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .business-facts > button,
  .business-facts > div {
    border-right: 1px solid rgba(255, 255, 255, 0.07);
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  }

  .forecast-section-label {
    align-items: flex-start;
    flex-direction: column;
    gap: 2px;
  }

  .forecast-actions {
    width: 100%;
  }

  .forecast-actions .v-btn {
    flex: 1 1 auto;
    margin-left: 0 !important;
  }

  .forecast-month {
    grid-template-columns: 68px minmax(55px, 1fr) 42px 93px;
    gap: 6px;
  }

  .forecast-month__amount,
  .forecast-month__name {
    font-size: 0.69rem;
  }

  .comparison-values {
    width: 100%;
    margin-top: 12px;
  }

  .comparison-values > div {
    min-width: calc(50% - 10px);
  }

  .forecast-audit-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .forecast-audit-strip > div,
  .forecast-audit-strip > button {
    border-bottom: 1px solid rgba(148, 163, 184, 0.11);
  }
}
</style>
